import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Settings as SettingsIcon, Shield, Activity, Search, Edit2, UserCheck, UserX, Plus, Minus, Award, User, Heart, Briefcase, Zap, CreditCard, TestTube, CheckCircle, XCircle, Mail, Server, Key, Send, Camera, Trash2, Download, AlertTriangle, FileText, Upload, X, Tag, Save, Trash, Clock, Eye, EyeOff, ChevronDown, ChevronUp, Cloud, RefreshCw, SunMoon, Bot, Mic, MessageSquare, FolderOpen } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import EmailSystemSettingsForm from '../components/EmailSystemSettingsForm';
import EmailCredentialModal from '../components/EmailCredentialModal';
import NextcloudCredentialsPanel from '../components/NextcloudCredentialsPanel';
import OneDriveCredentialsPanel from '../components/OneDriveCredentialsPanel';
import GradeSchemasPanel from '../components/settings/GradeSchemasPanel';
import NavigationPermissionsPanel from '../components/settings/NavigationPermissionsPanel';
import SecurityPanel from '../components/settings/SecurityPanel';
import MigrationStatusPanel from '../components/settings/MigrationStatusPanel';
import toast from 'react-hot-toast';
import { describeFintsTanProcedure } from '../utils/fintsTanHints';
import FintsTanChallengeModal, { type FintsTanMethodOption } from '../components/FintsTanChallengeModal';
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../components/ui';
import SettingsNavigation from './settings/SettingsNavigation';
import {
  getDefaultTabForArea,
  isSettingsTabId,
  SETTINGS_AREAS,
  type SettingsAreaId,
  type SettingsTabDefinition,
  type SettingsTabId
} from './settings/settingsStructure';
import {
  getTextScale,
  getThemePreference,
  setTextScale,
  setThemePreference,
  type LrTextScale,
  type LrThemePreference
} from '../lib/lrUiPreferences';
import {
  dispatchTomAssistantModeUpdated,
  type TomAssistantMode,
} from '../lib/tomAssistantMode';

/** Cookie-Session wie axios api.ts (withCredentials). Kein Bearer aus localStorage — nach HttpOnly-Login dort oft nichts, sonst „Bearer null“ → 403. */
function sessionFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return globalThis.fetch(input, { ...init, credentials: 'include' });
}

interface User {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  primary_role: 'admin' | 'teacher' | 'student' | 'guest';
  is_active: boolean;
  registration_status?: 'approved' | 'pending' | 'rejected';
  is_ldap_user: boolean;
  auth_method: 'ldap' | 'local';
  last_login?: string;
  last_auth_method?: 'ldap' | 'local';
  created_at: string;
  additional_roles_count: number;
}

interface UserStats {
  total: number;
  ldap: number;
  admin: number;
  teacher: number;
  student: number;
  recentLogins: number;
  inactive: number;
  pendingRegistrations?: number;
  multiRole: number;
  totalRoleAssignments: number;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  permissions: string[];
}

interface UserRole {
  id?: number;
  role_id?: number;
  name: string;
  display_name: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
}

interface SpecificAssignment {
  role_name: string;
  assignment_type: 'class' | 'department' | 'subject' | 'global';
  assignment_value: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
  notes?: string;
  granted_by_username?: string;
}

interface ClassInfo {
  id: number;
  class_name: string;
  grade_level: string;
  department: string;
  room: string;
  description: string;
  student_count?: number;
}

interface SubjectInfo {
  id: number;
  subject_name: string;
  subject_code: string;
  department: string;
  description: string;
}

interface PersonalData {
  firstName: string;
  lastName: string;
  displayName: string;
  paypalAccount: string;
  llmSettings: {
    herzlichkeit: number;
    foermlichkeit: number;
    bestimmtheit: number;
  };
  timezone: string;
  language: string;
}

interface EmailSystemSettings {
  id?: number;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: 'none' | 'tls' | 'ssl';
  smtp_auth_required: boolean;
  imap_host?: string;
  imap_port?: number;
  imap_secure?: 'none' | 'tls' | 'ssl';
  sender_name?: string;
  reply_to_email?: string;
  max_attachments_size?: number;
  enabled: boolean;
}

interface UserEmailCredentials {
  id?: number;
  email_address: string;
  email_username: string;
  display_name?: string;
  signature?: string;
  is_default: boolean;
  enabled: boolean;
  last_tested?: string;
  test_status?: 'untested' | 'success' | 'failed';
}

interface PayPalSettings {
  paypal_api_url: { value: string; description: string };
  paypal_client_id: { value: string; description: string };
  paypal_business_name: { value: string; description: string };
  paypal_currency: { value: string; description: string };
  paypal_return_url: { value: string; description: string };
  paypal_cancel_url: { value: string; description: string };
  paypal_webhook_url: { value: string; description: string };
  paypal_me_username?: { value: string; description: string };
  sepa_creditor_name?: { value: string; description: string };
  sepa_iban?: { value: string; description: string };
  sepa_bic?: { value: string; description: string };
  fints_url?: { value: string; description: string };
  fints_blz?: { value: string; description: string };
  fints_user_id?: { value: string; description: string };
  fints_account_iban?: { value: string; description: string };
  fints_product_id?: { value: string; description: string };
  fints_security_function?: { value: string; description: string };
  fints_tan_medium_name?: { value: string; description: string };
  fints_customer_id?: { value: string; description: string };
}

interface FintsBankCatalogHit {
  blz: string;
  name: string;
  ort: string;
  bic: string;
  url: string;
}

interface ImageSearchSourceSettings {
  wikipedia: boolean;
  pixabay: boolean;
  pexels: boolean;
  unsplash: boolean;
  google: boolean;
}

interface OnlyOfficeSettings {
  docsUrl: string;
  secretKey: string;
  disableSslVerify: boolean;
  internalUrl: string;
  callbackBaseUrl: string;
}

type NextcloudAccountMode = 'school_and_personal' | 'school_only' | 'custom_tenant';

interface NextcloudSettings {
  baseUrl: string;
  customServerUrl: string;
  accountMode: NextcloudAccountMode;
  disableSslVerify: boolean;
  useLernraketeCredentials: boolean;
}

interface StorageProvidersSettings {
  lernraketeEnabled: boolean;
  nextcloudEnabled: boolean;
  onedriveEnabled: boolean;
  nextcloudUseLernraketeCredentials: boolean;
}

type OnedriveAccountMode = 'school_and_personal' | 'school_only' | 'custom_tenant';

interface OnedriveSettings {
  clientId: string;
  clientSecret: string;
  accountMode: OnedriveAccountMode;
  customTenantId: string;
  redirectUri: string;
}

interface LlmModelProfile {
  key: string;
  label: string;
  description: string;
  model: string;
  temperature: number;
  maxTokens: number;
  enabled: boolean;
  allowedModels?: string[];
}

interface LlmModelAvailability {
  provider: string;
  providerConfigured: boolean;
  models: Array<{ model: string; available: boolean; reason?: string | null }>;
}

interface StudentFileBackfillRun {
  id: number;
  status: string;
  batch_size: number;
  cursor_file_id: number | null;
  queued_count: number;
  skipped_count: number;
  failed_count: number;
  force_reprocess: boolean;
  job_id: string | null;
  last_error?: string | null;
  updated_at: string;
  completed_at?: string | null;
  processing?: Record<string, number>;
}

interface BulkImportNameLlmSettings {
  enabled: boolean;
  model: string;
}

/** Antwort von GET /api/hub/status (nur Admin) */
interface HubConnectionInfo {
  configured: boolean;
  useForAssistant: boolean;
  llmViaHub?: boolean;
  llmMode?: string;
  baseUrl: string | null;
  tenantId: string;
  reachable: boolean;
  message?: string;
  ops?: {
    queueLag?: Record<string, number>;
    providerLatencyP95Ms?: number;
    providerErrorRate?: number;
    rateLimitRejections?: number;
  } | null;
  error?: string | null;
  apiKeyConfigured?: boolean;
  assistantModelHint?: string | null;
  assistantPriority?: string | null;
  /** GET /v1/admin/model-policy vom Hub-Core */
  modelPolicy?: {
    defaultModel?: string;
    purposes?: Record<string, string | null>;
    tasks?: Record<string, string | null>;
    tts?: { provider?: string; model?: string; voice?: string };
    notes?: string[];
  } | null;
  modelPolicyError?: string | null;
}

interface Student {
  ID: number;
  sophomorixUnid?: string;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
  Email?: string;
}

interface PreviewData {
  resolvedIdentifiers: {
    sophomorixUnid: string[];
    unid: string[];
    studentId: string[];
    id: string[];
    rfid: string[];
  };
  affectedTables: Array<{
    table: string;
    columns: string[];
    rowCount: number;
  }>;
  totalRows: number;
}

// User Data Management Component
function UserDataManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTablesModal, setShowTablesModal] = useState(false);
  const [userDataTables, setUserDataTables] = useState<Array<{table: string; columns: Array<{column: string; type: string}>}>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load students
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoading(true);
        const response = await api.get('/students');
        if (response.data.success) {
          setStudents(response.data.data || []);
        }
      } catch (error: any) {
        console.error('Error loading students:', error);
        toast.error('Fehler beim Laden der Schüler');
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, []);

  // Load user data tables
  const loadUserDataTables = async () => {
    try {
      const response = await api.get('/maintenance/user-data/tables');
      if (response.data.success) {
        setUserDataTables(response.data.data || []);
        setShowTablesModal(true);
      }
    } catch (error: any) {
      console.error('Error loading tables:', error);
      toast.error('Fehler beim Laden der Tabellen-Informationen');
    }
  };

  // Filter students
  const filteredStudents = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return students;
    }
    
    const search = searchTerm.toLowerCase().trim();
    
    return students.filter(student => {
      // Check all possible field names
      const vorname = (student.Vorname || '').toString();
      const name = (student.Name || '').toString();
      const klasse = (student.Klasse || '').toString();
      const unid = (student.sophomorixUnid || '').toString();
      const email = (student.Email || '').toString();
      const id = (student.ID || '').toString();
      
      return (
        vorname.toLowerCase().includes(search) ||
        name.toLowerCase().includes(search) ||
        klasse.toLowerCase().includes(search) ||
        unid.toLowerCase().includes(search) ||
        email.toLowerCase().includes(search) ||
        id.includes(search) ||
        `${vorname} ${name}`.toLowerCase().includes(search)
      );
    });
  }, [students, searchTerm]);

  // Toggle student selection
  const toggleStudent = (identifier: string) => {
    setSelectedIdentifiers(prev => 
      prev.includes(identifier)
        ? prev.filter(id => id !== identifier)
        : [...prev, identifier]
    );
  };

  // Preview what would be affected
  const handlePreview = async () => {
    if (selectedIdentifiers.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/maintenance/user-data/preview', {
        identifiers: selectedIdentifiers
      });
      if (response.data.success) {
        setPreviewData(response.data.data);
        setShowPreview(true);
      }
    } catch (error: any) {
      console.error('Error previewing data:', error);
      toast.error('Fehler beim Laden der Vorschau');
    } finally {
      setLoading(false);
    }
  };

  // Export user data
  const handleExport = async () => {
    if (selectedIdentifiers.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    try {
      setExporting(true);
      const response = await api.post('/maintenance/user-data/export', {
        identifiers: selectedIdentifiers
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/sql' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-data-export-${new Date().toISOString().replace(/[:.]/g, '-')}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export erfolgreich erstellt');
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast.error('Fehler beim Exportieren der Daten');
    } finally {
      setExporting(false);
    }
  };

  // Delete user data
  const handleDelete = async () => {
    if (selectedIdentifiers.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    try {
      setDeleting(true);
      
      // First, generate and download backup
      try {
        const backupResponse = await api.post('/maintenance/user-data/export', {
          identifiers: selectedIdentifiers
        }, {
          responseType: 'blob'
        });

        // Create download link for backup
        const blob = new Blob([backupResponse.data], { type: 'application/sql' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `user-data-backup-before-deletion-${new Date().toISOString().replace(/[:.]/g, '-')}.sql`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Backup wurde heruntergeladen');
      } catch (backupError: any) {
        console.error('Error creating backup:', backupError);
        toast.error('Fehler beim Erstellen des Backups, aber Löschung wird fortgesetzt');
      }

      // Now delete the data
      const response = await api.delete('/maintenance/user-data', {
        data: {
          identifiers: selectedIdentifiers,
          confirm: true
        }
      });

      if (response.data.success) {
        toast.success(`Daten erfolgreich gelöscht. Backup wurde heruntergeladen.`);
        setSelectedIdentifiers([]);
        setShowDeleteConfirm(false);
        setPreviewData(null);
        setShowPreview(false);
        
        // Reload students list
        const reloadResponse = await api.get('/students');
        if (reloadResponse.data.success) {
          setStudents(reloadResponse.data.data || []);
        }
      }
    } catch (error: any) {
      console.error('Error deleting data:', error);
      toast.error('Fehler beim Löschen der Daten');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <div className="flex-shrink-0">
          <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
        </div>
        <div className="ml-3">
          <h3 className="text-lg font-medium text-gray-900">
            DSGVO User-Daten-Verwaltung
          </h3>
          <p className="text-sm text-gray-500">
            Export und Löschung von Benutzerdaten
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Wichtiger Hinweis:</p>
              <p>Diese Funktion dient der DSGVO-konformen Löschung von Benutzerdaten. Vor dem Löschen wird automatisch ein Backup erstellt. Die Löschung kann nicht rückgängig gemacht werden.</p>
            </div>
          </div>
        </div>

        {/* Student Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Schüler auswählen (nach Name, Klasse oder sophomorixUnid suchen)
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suche nach Name, Klasse oder sophomorixUnid..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Student List */}
        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Lade Schüler...</div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-4 text-center text-gray-500">Keine Schüler gefunden</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredStudents.map((student: Student) => {
                const identifier = student.sophomorixUnid || String(student.ID);
                const isSelected = selectedIdentifiers.includes(identifier);
                return (
                  <div
                    key={student.ID}
                    className={`p-3 hover:bg-gray-50 cursor-pointer flex items-center ${isSelected ? 'bg-blue-50' : ''}`}
                    onClick={() => toggleStudent(identifier)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleStudent(identifier);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {student.Vorname} {student.Name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {student.Klasse} • {student.sophomorixUnid || `ID: ${student.ID}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedIdentifiers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>{selectedIdentifiers.length}</strong> Schüler ausgewählt
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handlePreview}
            disabled={selectedIdentifiers.length === 0 || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="h-4 w-4" />
            Vorschau
          </button>
          <button
            onClick={handleExport}
            disabled={selectedIdentifiers.length === 0 || exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exportiere...' : 'Exportieren'}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={selectedIdentifiers.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-4 w-4" />
            Löschen
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Importieren
          </button>
          <button
            onClick={loadUserDataTables}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FileText className="h-4 w-4" />
            Tabellen anzeigen
          </button>
        </div>

        {/* Preview Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Vorschau der betroffenen Daten</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Gefundene Identifikatoren:</p>
                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <p><strong>sophomorixUnid:</strong> {previewData.resolvedIdentifiers.sophomorixUnid.join(', ') || 'Keine'}</p>
                    <p><strong>Unid/Student_ID:</strong> {previewData.resolvedIdentifiers.studentId.join(', ') || 'Keine'}</p>
                    <p><strong>ID:</strong> {previewData.resolvedIdentifiers.id.join(', ') || 'Keine'}</p>
                    <p><strong>RFID:</strong> {previewData.resolvedIdentifiers.rfid.join(', ') || 'Keine'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Betroffene Tabellen:</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tabelle</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Spalten</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Zeilen</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.affectedTables.map((table) => (
                          <tr key={table.table}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{table.table}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{table.columns.join(', ')}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{table.rowCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    <strong>Gesamt:</strong> {previewData.totalRows} Zeilen in {previewData.affectedTables.length} Tabellen
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Löschung bestätigen</h3>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  Möchten Sie wirklich die Daten von <strong>{selectedIdentifiers.length}</strong> ausgewählten Schüler(n) löschen?
                </p>
                <p className="text-sm text-gray-600">
                  Ein Backup wird automatisch erstellt, bevor die Daten gelöscht werden. Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Lösche...' : 'Endgültig löschen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">SQL-Datei importieren</h3>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SQL-Datei auswählen
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".sql"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      try {
                        setImporting(true);
                        const formData = new FormData();
                        formData.append('file', file);

                        const response = await api.post('/maintenance/user-data/import', formData, {
                          headers: {
                            'Content-Type': 'multipart/form-data'
                          }
                        });

                        if (response.data.success) {
                          const { inserted, skipped, errors } = response.data.data;
                          let message = `Import erfolgreich: ${inserted} Zeilen eingefügt`;
                          if (skipped > 0) {
                            message += `, ${skipped} Duplikate übersprungen`;
                          }
                          if (errors > 0) {
                            message += `, ${errors} Fehler`;
                          }
                          toast.success(message);
                          setShowImportModal(false);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          
                          // Reload students list
                          const reloadResponse = await api.get('/students');
                          if (reloadResponse.data.success) {
                            setStudents(reloadResponse.data.data || []);
                          }
                        }
                      } catch (error: any) {
                        console.error('Error importing data:', error);
                        toast.error(error.response?.data?.message || 'Fehler beim Importieren der Daten');
                      } finally {
                        setImporting(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    disabled={importing}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Hinweis:</strong> Die SQL-Datei sollte INSERT-Statements enthalten, die von dieser Funktion exportiert wurden.
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={importing}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tables Info Modal */}
        {showTablesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Berücksichtigte Tabellen</h3>
                <button
                  onClick={() => setShowTablesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Info:</strong> Die folgenden Tabellen werden automatisch beim Export und Löschen berücksichtigt. 
                    Tabellen werden erkannt, wenn sie eine der folgenden Spalten enthalten: 
                    <code className="ml-1 px-1 bg-blue-100 rounded">sophomorixUnid</code>, 
                    <code className="ml-1 px-1 bg-blue-100 rounded">Unid</code>, 
                    <code className="ml-1 px-1 bg-blue-100 rounded">Student_ID</code>, 
                    <code className="ml-1 px-1 bg-blue-100 rounded">student_id</code>, 
                    <code className="ml-1 px-1 bg-blue-100 rounded">user_id</code>, 
                    <code className="ml-1 px-1 bg-blue-100 rounded">CardID</code> oder 
                    <code className="ml-1 px-1 bg-blue-100 rounded">RFID</code>.
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Gefundene Tabellen ({userDataTables.length}):
                  </p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tabelle</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Relevante Spalten</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {userDataTables.map((tableInfo) => (
                          <tr key={tableInfo.table}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{tableInfo.table}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {tableInfo.columns.map((col, idx) => (
                                <span key={idx} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-gray-100 rounded text-xs">
                                  {col.column}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTablesModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTabId>('organization');

  const normalizedRoles = useMemo(() => {
    const roleValues = [
      ...(Array.isArray((user as any)?.allRoles) ? (user as any).allRoles : []),
      ...(Array.isArray((user as any)?.roles) ? (user as any).roles : []),
      user?.role
    ];
    return new Set(roleValues.filter(Boolean).map((role) => String(role).toLowerCase()));
  }, [user]);

  const isAdmin = normalizedRoles.has('admin') || normalizedRoles.has('administrator');
  const canManageSchoolSettings = isAdmin || normalizedRoles.has('schulleitung');
  const canManageUsers = isAdmin;

  // Tab aus URL (z. B. /settings?tab=email-signature) beim Laden setzen
  useEffect(() => {
    const tabRaw = searchParams.get('tab');
    const mappedLegacyTab = tabRaw === 'paypal' ? 'geldkonten' : tabRaw === 'general' ? 'organization' : tabRaw;
    if (mappedLegacyTab && isSettingsTabId(mappedLegacyTab)) {
      setActiveTab(mappedLegacyTab);
    }
  }, [searchParams]);
  
  // Email Signature State
  const [emailSignature, setEmailSignature] = useState({
    text: '',
    html: '',
    imageUrl: '',
    enabled: false
  });
  const [emailSignatureLoading, setEmailSignatureLoading] = useState(false);
  const [emailSignatureSaving, setEmailSignatureSaving] = useState(false);
  const [emailContentCheckEnabled, setEmailContentCheckEnabled] = useState(true);
  const [emailContentCheckSaving, setEmailContentCheckSaving] = useState(false);
  const [contentCheckExcludedRecipients, setContentCheckExcludedRecipients] = useState<string[]>([]);
  const [contentCheckExclusionsSaving, setContentCheckExclusionsSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [ldapTeachersProvisioning, setLdapTeachersProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [personalData, setPersonalData] = useState<PersonalData | null>(null);
  const [personalDataLoading, setPersonalDataLoading] = useState(false);
  const [uiThemePreference, setUiThemePreference] = useState<LrThemePreference>(() => getThemePreference());
  const [uiTextScale, setUiTextScale] = useState<LrTextScale>(() => getTextScale());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [specificAssignments, setSpecificAssignments] = useState<SpecificAssignment[]>([]);
  const [availableClasses, setAvailableClasses] = useState<ClassInfo[]>([]);
  const [availableSubjects, setAvailableSubjects] = useState<SubjectInfo[]>([]);
  
  // PayPal Settings State (Partial = DB kann Keys fehlen; {} nach Ladefehler damit Formular/SEPA/FinTS nutzbar bleiben)
  const [paypalSettings, setPaypalSettings] = useState<Partial<PayPalSettings> | null>(null);
  const [geldkontenFetchError, setGeldkontenFetchError] = useState<string | null>(null);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalClientSecret, setPaypalClientSecret] = useState('');
  const [fintsPin, setFintsPin] = useState('');
  const [fintsBankSearchQuery, setFintsBankSearchQuery] = useState('');
  const [fintsBankHits, setFintsBankHits] = useState<FintsBankCatalogHit[]>([]);
  const [fintsBankSearchLoading, setFintsBankSearchLoading] = useState(false);
  const fintsUrlInputRef = useRef<HTMLInputElement>(null);
  const fintsBlzInputRef = useRef<HTMLInputElement>(null);
  const [connectionTestResult, setConnectionTestResult] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [fintsTestResult, setFintsTestResult] = useState<any>(null);
  const [testingFintsConnection, setTestingFintsConnection] = useState(false);
  const [fintsTestTanToken, setFintsTestTanToken] = useState<string | null>(null);
  const [fintsTestTanChallengeText, setFintsTestTanChallengeText] = useState('');
  const [fintsTestTanValue, setFintsTestTanValue] = useState('');
  const [fintsTestTanChallengeMediaB64, setFintsTestTanChallengeMediaB64] = useState<string | null>(null);
  const [fintsTestTanChallengeMediaMime, setFintsTestTanChallengeMediaMime] = useState<string | null>(null);
  const [fintsTestTanChallengeHhduc, setFintsTestTanChallengeHhduc] = useState<string | null>(null);
  const [fintsTestTanDecoupled, setFintsTestTanDecoupled] = useState(false);
  const [fintsTestTanMethodOptions, setFintsTestTanMethodOptions] = useState<FintsTanMethodOption[]>([]);
  const [fintsTestSelectedTanMethod, setFintsTestSelectedTanMethod] = useState('');
  const [fintsTestTanProcessCode, setFintsTestTanProcessCode] = useState<string | null>(null);
  const [fintsTestTanBankMediumName, setFintsTestTanBankMediumName] = useState<string | null>(null);
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);

  // Email Settings State
  const [emailSystemSettings, setEmailSystemSettings] = useState<EmailSystemSettings | null>(null);
  const [emailSystemLoading, setEmailSystemLoading] = useState(false);
  const [userEmailCredentials, setUserEmailCredentials] = useState<UserEmailCredentials[]>([]);
  const [userEmailLoading, setUserEmailLoading] = useState(false);
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false);
  const [editingEmailCredential, setEditingEmailCredential] = useState<UserEmailCredentials | null>(null);
  const [emailTestResults, setEmailTestResults] = useState<{[key: string]: any}>({});
  const [testingEmails, setTestingEmails] = useState<Set<string>>(new Set());
  
  // Student Email Policy State
  const [studentEmailMinAge, setStudentEmailMinAge] = useState<number>(16);
  const [queuePermissions, setQueuePermissions] = useState<any[]>([]);
  const [queueAvailableRoles, setQueueAvailableRoles] = useState<string[]>([]);
  const [queueAvailableUsers, setQueueAvailableUsers] = useState<any[]>([]);
  const [queuePermissionsLoading, setQueuePermissionsLoading] = useState(false);
  const [newPermissionType, setNewPermissionType] = useState<'role' | 'user'>('role');
  const [newPermissionValue, setNewPermissionValue] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserSearchResults, setShowUserSearchResults] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);
  const [studentEmailPolicyLoading, setStudentEmailPolicyLoading] = useState(false);
  /** Schulweit: Schüler der Klasse „attic“ in Auswahllisten ausblenden */
  const [hideInactiveStudentsFromAttic, setHideInactiveStudentsFromAttic] = useState(false);
  const [hideInactiveStudentsLoading, setHideInactiveStudentsLoading] = useState(false);
  const [scheduledEmailTimezone, setScheduledEmailTimezone] = useState<string | null>(null);
  const [scheduledEmailTimezoneLoading, setScheduledEmailTimezoneLoading] = useState(false);
  const [scheduledEmailTimezoneSaving, setScheduledEmailTimezoneSaving] = useState(false);
  const [logLevel, setLogLevel] = useState<string>('info');
  const [logLevelAllowedLevels, setLogLevelAllowedLevels] = useState<string[]>(['trace', 'debug', 'info', 'warn', 'error', 'fatal']);
  const [logLevelLoading, setLogLevelLoading] = useState(false);
  const [logLevelSaving, setLogLevelSaving] = useState(false);
  const [imageSearchSourceSettings, setImageSearchSourceSettings] = useState<ImageSearchSourceSettings>({
    wikipedia: true,
    pixabay: false,
    pexels: false,
    unsplash: false,
    google: false
  });
  const [imageSearchSourceSettingsLoading, setImageSearchSourceSettingsLoading] = useState(false);
  const [imageSearchSourceSettingsSaving, setImageSearchSourceSettingsSaving] = useState(false);
  const [tomAssistantMode, setTomAssistantMode] = useState<TomAssistantMode>('voice');
  const [tomAssistantModeLoading, setTomAssistantModeLoading] = useState(false);
  const [tomAssistantModeSaving, setTomAssistantModeSaving] = useState(false);
  const [selfRegistrationEnabled, setSelfRegistrationEnabled] = useState(false);
  const [selfRegistrationLoading, setSelfRegistrationLoading] = useState(false);
  const [selfRegistrationSaving, setSelfRegistrationSaving] = useState(false);
  const [selfRegistrationUpdatedBy, setSelfRegistrationUpdatedBy] = useState<string | null>(null);
  const [selfRegistrationUpdatedAt, setSelfRegistrationUpdatedAt] = useState<string | null>(null);
  const [parentPortalEnabled, setParentPortalEnabled] = useState(true);
  const [parentPortalLoading, setParentPortalLoading] = useState(false);
  const [parentPortalSaving, setParentPortalSaving] = useState(false);
  const [parentPortalUpdatedBy, setParentPortalUpdatedBy] = useState<string | null>(null);
  const [parentPortalUpdatedAt, setParentPortalUpdatedAt] = useState<string | null>(null);
  const [onlyofficeSettings, setOnlyofficeSettings] = useState<OnlyOfficeSettings>({
    docsUrl: '',
    secretKey: '',
    disableSslVerify: false,
    internalUrl: '',
    callbackBaseUrl: ''
  });
  const [onlyofficeLoading, setOnlyofficeLoading] = useState(false);
  const [onlyofficeSaving, setOnlyofficeSaving] = useState(false);
  const [onlyofficeShowSecret, setOnlyofficeShowSecret] = useState(false);
  const [onlyofficeAdvancedOpen, setOnlyofficeAdvancedOpen] = useState(false);
  const [storageProviders, setStorageProviders] = useState<StorageProvidersSettings>({
    lernraketeEnabled: true,
    nextcloudEnabled: false,
    onedriveEnabled: false,
    nextcloudUseLernraketeCredentials: false
  });
  const [storageProvidersLoading, setStorageProvidersLoading] = useState(false);
  const [storageProvidersSaving, setStorageProvidersSaving] = useState(false);
  const storageProvidersFetchId = useRef(0);
  const storageProvidersSaveQueue = useRef(Promise.resolve());
  const [nextcloudSettings, setNextcloudSettings] = useState<NextcloudSettings>({
    baseUrl: '',
    customServerUrl: '',
    accountMode: 'school_and_personal',
    disableSslVerify: false,
    useLernraketeCredentials: false
  });
  const [nextcloudLoading, setNextcloudLoading] = useState(false);
  const [nextcloudSaving, setNextcloudSaving] = useState(false);
  const [nextcloudAdminConfigured, setNextcloudAdminConfigured] = useState(false);
  const [onedriveSettings, setOnedriveSettings] = useState<OnedriveSettings>({
    clientId: '',
    clientSecret: '',
    accountMode: 'school_and_personal',
    customTenantId: '',
    redirectUri: ''
  });
  const [onedriveLoading, setOnedriveLoading] = useState(false);
  const [onedriveSaving, setOnedriveSaving] = useState(false);
  const [onedriveShowSecret, setOnedriveShowSecret] = useState(false);
  const [onedriveAdminConfigured, setOnedriveAdminConfigured] = useState(false);
  const [storagePublic, setStoragePublic] = useState<{
    lernrakete: { enabled: boolean };
    nextcloud: { enabled: boolean };
    onedrive: { enabled: boolean };
  } | null>(null);
  const [llmProfiles, setLlmProfiles] = useState<LlmModelProfile[]>([]);
  const [llmAllowedModels, setLlmAllowedModels] = useState<string[]>(['gpt-5', 'gpt-5-mini', 'gpt-4o-mini', 'gpt-4.1-mini']);
  const [llmAvailability, setLlmAvailability] = useState<LlmModelAvailability | null>(null);
  const [llmProfilesLoading, setLlmProfilesLoading] = useState(false);
  const [llmProfilesSaving, setLlmProfilesSaving] = useState(false);
  const [studentFileBackfills, setStudentFileBackfills] = useState<StudentFileBackfillRun[]>([]);
  const [studentFileBackfillsLoading, setStudentFileBackfillsLoading] = useState(false);
  const [studentFileBackfillsStarting, setStudentFileBackfillsStarting] = useState(false);
  const [bulkImportNameLlmSettings, setBulkImportNameLlmSettings] = useState<BulkImportNameLlmSettings>({
    enabled: false,
    model: 'gpt-4o-mini'
  });
  const [bulkImportNameLlmLoading, setBulkImportNameLlmLoading] = useState(false);
  const [bulkImportNameLlmSaving, setBulkImportNameLlmSaving] = useState(false);

  const [hubConnection, setHubConnection] = useState<HubConnectionInfo | null>(null);
  const [hubConnectionLoading, setHubConnectionLoading] = useState(false);

  // Email Labels Management
  const [emailLabels, setEmailLabels] = useState<any[]>([]);
  const [emailLabelsLoading, setEmailLabelsLoading] = useState(false);
  const [editingLabel, setEditingLabel] = useState<any | null>(null);
  const [newLabel, setNewLabel] = useState({
    name: '',
    description: '',
    color: '#6B7280',
    icon: 'tag',
    llm_criteria: {
      keywords: [] as string[],
      patterns: [] as string[],
      context_rules: [] as string[],
      priority: 'medium' as 'low' | 'medium' | 'high'
    }
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [newContextRule, setNewContextRule] = useState('');

  const getUserRoleId = (role: UserRole): number | null => {
    if (typeof role.id === 'number' && Number.isFinite(role.id)) {
      return role.id;
    }
    if (typeof role.role_id === 'number' && Number.isFinite(role.role_id)) {
      return role.role_id;
    }
    return null;
  };

  const canConfigureNotenschemata =
    isAdmin ||
    normalizedRoles.has('klassenlehrer') ||
    normalizedRoles.has('teacher') ||
    normalizedRoles.has('lehrer') ||
    normalizedRoles.has('abteilungsleiter') ||
    normalizedRoles.has('schulleitung');

  const tabs = useMemo<Array<SettingsTabDefinition & { icon: React.ElementType }>>(
    () => [
      { id: 'personal', label: 'Persönliche Daten', icon: User, area: 'personal', visible: true },
      { id: 'appearance', label: 'Darstellung', icon: SunMoon, area: 'personal', visible: true },
      { id: 'security', label: 'Sicherheit', icon: Shield, area: 'personal', visible: true },
      { id: 'email-signature', label: 'E-Mail-Signatur', icon: Mail, area: 'personal', visible: true },
      {
        id: 'organization',
        label: 'Schulweite Einstellungen',
        icon: SettingsIcon,
        area: 'school',
        visible: canManageSchoolSettings
      },
      { id: 'users', label: 'Benutzerverwaltung', icon: Users, area: 'school', visible: canManageUsers },
      { id: 'noten', label: 'Notenschemata', icon: Award, area: 'school', visible: canConfigureNotenschemata },
      { id: 'system-admin', label: 'Systemeinstellungen', icon: Server, area: 'system', visible: isAdmin },
      { id: 'geldkonten', label: 'Geldkonten', icon: CreditCard, area: 'system', visible: isAdmin },
      { id: 'maintenance', label: 'Wartung', icon: Shield, area: 'system', visible: isAdmin },
      {
        id: 'navigation-permissions',
        label: 'Navigations-Berechtigungen',
        icon: Shield,
        area: 'system',
        visible: isAdmin
      }
    ],
    [canConfigureNotenschemata, canManageSchoolSettings, canManageUsers, isAdmin]
  );

  useEffect(() => {
    const currentTab = tabs.find((tab) => tab.id === activeTab);
    if (currentTab?.visible) return;

    const fallbackTab = tabs.find((tab) => tab.visible);
    if (fallbackTab) {
      setActiveTab(fallbackTab.id);
    }
  }, [activeTab, tabs]);

  const activeArea = useMemo<SettingsAreaId>(() => {
    return tabs.find((tab) => tab.id === activeTab)?.area ?? 'personal';
  }, [activeTab, tabs]);

  const handleAreaChange = (nextArea: SettingsAreaId) => {
    const nextTab = getDefaultTabForArea(nextArea, tabs);
    if (!nextTab) return;
    setActiveTab(nextTab);
    setSearchParams({ tab: nextTab });
  };

  const handleTabChange = (nextTab: SettingsTabId) => {
    setActiveTab(nextTab);
    setSearchParams({ tab: nextTab });
  };
  
  // Load email signature and content-check setting
  useEffect(() => {
    if (activeTab === 'email-signature') {
      const loadEmailSignature = async () => {
        setEmailSignatureLoading(true);
        try {
          const response = await api.get('/settings/email-signature');
          if (response.data?.success) {
            setEmailSignature(response.data.data);
          }
        } catch (error) {
          console.error('Error loading email signature:', error);
        } finally {
          setEmailSignatureLoading(false);
        }
      };
      loadEmailSignature();
      const loadEmailContentCheck = async () => {
        try {
          const res = await api.get('/settings/email-content-check');
          if (res.data?.success && res.data?.data) {
            const d = res.data.data;
            if (d.enabled !== undefined) setEmailContentCheckEnabled(d.enabled);
            setContentCheckExcludedRecipients(Array.isArray(d.excludedRecipients) ? d.excludedRecipients : []);
          }
        } catch (_) {}
      };
      loadEmailContentCheck();
    }
  }, [activeTab]);
  
  // Save email signature
  const saveEmailSignature = async () => {
    setEmailSignatureSaving(true);
    try {
      const response = await api.post('/settings/email-signature', emailSignature);
      if (response.data?.success) {
        toast.success('E-Mail-Signatur gespeichert');
      } else {
        toast.error('Fehler beim Speichern der Signatur');
      }
    } catch (error: any) {
      console.error('Error saving email signature:', error);
      toast.error(error?.response?.data?.message || 'Fehler beim Speichern der Signatur');
    } finally {
      setEmailSignatureSaving(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter === 'pending_approval') {
        params.append('pending_only', 'true');
      } else if (roleFilter) {
        params.append('role', roleFilter);
      }
      
      const response = await sessionFetch(`/api/users?${params}`, {
        headers: {
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await sessionFetch('/api/users/stats', {
        headers: {
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserStats(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const handleLdapTeachersProvision = async () => {
    if (
      !window.confirm(
        'LDAP nach Lehrkräften in OU=Teachers durchsuchen und fehlende Konten in der Benutzerdatenbank anlegen? Bestehende Benutzer werden nicht geändert.'
      )
    ) {
      return;
    }

    setLdapTeachersProvisioning(true);
    setError(null);
    try {
      const response = await sessionFetch('/api/users/ldap-teachers-provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      toast.success(data.message || 'LDAP-Lehrkräfte wurden verarbeitet.');
      await fetchUsers();
      await fetchUserStats();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LDAP-Import fehlgeschlagen';
      setError(message);
      toast.error(message);
    } finally {
      setLdapTeachersProvisioning(false);
    }
  };

  const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const endpoint = currentStatus ? `/api/users/${userId}` : `/api/users/${userId}/activate`;
      const method = currentStatus ? 'DELETE' : 'POST';

      const response = await sessionFetch(endpoint, {
        method,
        headers: {
        },
      });

      if (response.ok) {
        fetchUsers(); // Refresh the list
        fetchUserStats(); // Refresh stats
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Ändern des Benutzerstatus');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('Fehler beim Ändern des Benutzerstatus');
    }
  };

  const approveUser = async (userId: number) => {
    try {
      const response = await sessionFetch(`/api/users/${userId}/approve`, {
        method: 'POST',
      });
      if (response.ok) {
        await fetchUsers();
        await fetchUserStats();
        toast.success('Benutzer wurde freigeschaltet');
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Freischalten des Benutzers');
      }
    } catch (error) {
      console.error('Error approving user:', error);
      setError('Fehler beim Freischalten des Benutzers');
    }
  };

  const rejectUser = async (userId: number) => {
    try {
      const response = await sessionFetch(`/api/users/${userId}/reject`, {
        method: 'POST',
      });
      if (response.ok) {
        await fetchUsers();
        await fetchUserStats();
        toast.success('Registrierung wurde abgelehnt');
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Ablehnen der Registrierung');
      }
    } catch (error) {
      console.error('Error rejecting user:', error);
      setError('Fehler beim Ablehnen der Registrierung');
    }
  };

  const resetUserMfa = async (username: string) => {
    const confirmed = window.confirm(
      `Möchten Sie 2FA für ${username} wirklich zurücksetzen?\n\nDer Benutzer muss danach den OTP-Schutz neu einrichten.`
    );
    if (!confirmed) return;

    try {
      const response = await sessionFetch(`/api/auth/mfa/admin/users/${encodeURIComponent(username)}/reset`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok && data?.success) {
        toast.success(`2FA für ${username} wurde zurückgesetzt.`);
        return;
      }
      toast.error(data?.message || data?.error || '2FA-Reset fehlgeschlagen');
    } catch (error: any) {
      toast.error(error?.message || '2FA-Reset fehlgeschlagen');
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await sessionFetch('/api/roles', {
        headers: {
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableRoles(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching available roles:', error);
    }
  };

  const fetchUserRoles = async (userId: number) => {
    try {
      const response = await sessionFetch(`/api/roles/user/${userId}`, {
        headers: {
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const normalizedAssignedRoles = (data.data.assignedRoles || []).map((role: UserRole) => ({
            ...role,
            id: typeof role.id === 'number' ? role.id : role.role_id
          }));
          setUserRoles(normalizedAssignedRoles);
        }
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
    }
  };

  const assignRole = async (userId: number, roleId: number) => {
    try {
      const response = await sessionFetch(`/api/roles/${roleId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        fetchUserRoles(userId);
        fetchUsers(); // Refresh main list to update role counts
        fetchUserStats();
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Zuweisen der Rolle');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      setError('Fehler beim Zuweisen der Rolle');
    }
  };

  const revokeRole = async (userId: number, roleId: number) => {
    try {
      const response = await sessionFetch(`/api/roles/${roleId}/revoke`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        fetchUserRoles(userId);
        fetchUsers(); // Refresh main list to update role counts
        fetchUserStats();
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Entziehen der Rolle');
      }
    } catch (error) {
      console.error('Error revoking role:', error);
      setError('Fehler beim Entziehen der Rolle');
    }
  };

  const fetchSpecificAssignments = async (userId: number) => {
    try {
      const response = await sessionFetch(`/api/assignments/user/${userId}`, {
        headers: {
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSpecificAssignments([
            ...data.data.classes,
            ...data.data.departments,
            ...data.data.subjects,
            ...data.data.global
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching specific assignments:', error);
    }
  };

  const fetchAssignmentOptions = async () => {
    try {
      
      // Fetch classes, subjects, and departments in parallel
      const [classesRes, subjectsRes, departmentsRes] = await Promise.all([
        sessionFetch('/api/assignments/classes', {
        }),
        sessionFetch('/api/assignments/subjects', {
        }),
        sessionFetch('/api/assignments/departments', {
        })
      ]);

      if (classesRes.ok) {
        const classesData = await classesRes.json();
        if (classesData.success) {
          setAvailableClasses(classesData.data);
        }
      }

      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        if (subjectsData.success) {
          setAvailableSubjects(subjectsData.data);
        }
      }

      if (departmentsRes.ok) {
        const departmentsData = await departmentsRes.json();
        if (departmentsData.success) {
          setAvailableDepartments(departmentsData.data.map((d: any) => d.name));
        }
      }
    } catch (error) {
      console.error('Error fetching assignment options:', error);
    }
  };

  const assignSpecificRole = async (userId: number, roleName: string, assignmentType: string, assignmentValue: string) => {
    try {
      const response = await sessionFetch('/api/assignments/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          roleName,
          assignmentType,
          assignmentValue: assignmentValue || null
        }),
      });

      if (response.ok) {
        await fetchSpecificAssignments(userId);
        await fetchUsers(); // Refresh main list
        setError(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Zuweisen der spezifischen Rolle');
      }
    } catch (error) {
      console.error('Error assigning specific role:', error);
      setError('Fehler beim Zuweisen der spezifischen Rolle');
    }
  };

  const revokeSpecificRole = async (userId: number, roleName: string, assignmentType: string, assignmentValue: string) => {
    try {
      const response = await sessionFetch('/api/assignments/revoke', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          roleName,
          assignmentType,
          assignmentValue: assignmentValue || null
        }),
      });

      if (response.ok) {
        await fetchSpecificAssignments(userId);
        await fetchUsers(); // Refresh main list
        setError(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Entziehen der spezifischen Rolle');
      }
    } catch (error) {
      console.error('Error revoking specific role:', error);
      setError('Fehler beim Entziehen der spezifischen Rolle');
    }
  };

  const openRoleModal = async (user: User) => {
    setSelectedUser(user);
    setShowRoleModal(true);
    await fetchUserRoles(user.id);
    await fetchAvailableRoles();
    await fetchSpecificAssignments(user.id);
    await fetchAssignmentOptions();
  };

  const fetchPersonalData = async () => {
    setPersonalDataLoading(true);
    setError(null);
    try {
      const response = await sessionFetch('/api/profile', {
        headers: {
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setPersonalData({
          firstName: data.data.firstName || '',
          lastName: data.data.lastName || '',
          displayName: data.data.displayName || '',
          paypalAccount: data.data.paypalAccount || '',
          llmSettings: data.data.llmSettings || {
            herzlichkeit: 50,
            foermlichkeit: 50,
            bestimmtheit: 50
          },
          timezone: data.data.timezone || 'Europe/Berlin',
          language: data.data.language || 'de'
        });
      }
    } catch (error) {
      console.error('Error fetching personal data:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Laden der persönlichen Daten');
    } finally {
      setPersonalDataLoading(false);
    }
  };

  // PayPal Settings Functions
  const fetchPaypalSettings = async () => {
    if (user?.role !== 'admin') return;

    setPaypalLoading(true);
    setGeldkontenFetchError(null);
    try {
      const response = await sessionFetch('/api/payments/settings', {
        headers: {
        }
      });

      let payload: { success?: boolean; message?: string; data?: Partial<PayPalSettings> } = {};
      try {
        payload = await response.json();
      } catch {
        /* nicht JSON */
      }

      if (!response.ok) {
        const msg =
          payload.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        setGeldkontenFetchError(msg);
        setPaypalSettings({});
        return;
      }

      if (payload.success && payload.data) {
        setPaypalSettings(payload.data);
      } else {
        setGeldkontenFetchError(payload.message || 'Fehler beim Laden der Geldkonten-Einstellungen');
        setPaypalSettings({});
      }
    } catch (error) {
      console.error('Error fetching account settings:', error);
      setGeldkontenFetchError(
        error instanceof Error ? error.message : 'Fehler beim Laden der Geldkonten-Einstellungen'
      );
      setPaypalSettings({});
    } finally {
      setPaypalLoading(false);
    }
  };

  const updatePaypalSettings = async (settings: any) => {
    if (user?.role !== 'admin') return;

    setPaypalLoading(true);
    setGeldkontenFetchError(null);
    try {
      
      // Include client secret if provided
      const payload = { ...settings };
      if (paypalClientSecret.trim()) {
        payload.paypal_client_secret = paypalClientSecret;
      }
      if (fintsPin.trim()) {
        payload.fints_pin = fintsPin;
      }
      
      const response = await sessionFetch('/api/payments/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        // Refresh settings
        await fetchPaypalSettings();
        setPaypalClientSecret(''); // Clear secret field
        setFintsPin('');
        alert('Geldkonten-Einstellungen erfolgreich aktualisiert!');
      } else {
        setGeldkontenFetchError(data.message || 'Fehler beim Aktualisieren der Geldkonten-Einstellungen');
      }
    } catch (error) {
      console.error('Error updating account settings:', error);
      setGeldkontenFetchError(
        error instanceof Error ? error.message : 'Fehler beim Aktualisieren der Geldkonten-Einstellungen'
      );
    } finally {
      setPaypalLoading(false);
    }
  };

  const testPaypalConnection = async () => {
    if (user?.role !== 'admin') return;
    
    setTestingConnection(true);
    setConnectionTestResult(null);
    setFintsTestResult(null);
    setFintsTestTanToken(null);
    setFintsTestTanChallengeText('');
    setFintsTestTanValue('');
    setFintsTestTanChallengeMediaB64(null);
    setFintsTestTanChallengeMediaMime(null);
    setFintsTestTanChallengeHhduc(null);
    setFintsTestTanDecoupled(false);
    setFintsTestTanMethodOptions([]);
    setFintsTestSelectedTanMethod('');
    try {
      const response = await sessionFetch('/api/payments/test-connection', {
        method: 'POST',
        headers: {
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setConnectionTestResult(data);
    } catch (error) {
      console.error('Error testing PayPal connection:', error);
      setConnectionTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Fehler beim Testen der PayPal-Verbindung'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const applyFintsTestTanPayload = (d: Record<string, unknown>) => {
    const methods = Array.isArray(d.tan_methods) ? (d.tan_methods as FintsTanMethodOption[]) : [];
    setFintsTestTanMethodOptions(methods);
    const cur = String(d.tan_security_function || '').trim();
    const fallback = methods[0]?.id ? String(methods[0].id) : '';
    setFintsTestSelectedTanMethod(cur || fallback);
    const pc = String(d.tan_process_code ?? '').trim().slice(0, 1);
    setFintsTestTanProcessCode(pc || null);
    const bm = String(d.tan_medium_name ?? '').trim();
    setFintsTestTanBankMediumName(bm || null);
  };

  const clearFintsTestTanUi = () => {
    setFintsTestTanToken(null);
    setFintsTestTanChallengeText('');
    setFintsTestTanValue('');
    setFintsTestTanChallengeMediaB64(null);
    setFintsTestTanChallengeMediaMime(null);
    setFintsTestTanChallengeHhduc(null);
    setFintsTestTanDecoupled(false);
    setFintsTestTanMethodOptions([]);
    setFintsTestSelectedTanMethod('');
    setFintsTestTanProcessCode(null);
    setFintsTestTanBankMediumName(null);
  };

  const fintsTestTanProcedureHint = useMemo(() => {
    const sel = fintsTestTanMethodOptions.find((m) => m.id === fintsTestSelectedTanMethod);
    const mtp = sel?.tan_process != null ? String(sel.tan_process).trim().slice(0, 1) : null;
    return describeFintsTanProcedure({
      decoupled: fintsTestTanDecoupled,
      tanProcessCode: fintsTestTanProcessCode,
      hasChallengeImage: Boolean(
        fintsTestTanChallengeMediaB64 && fintsTestTanChallengeMediaMime?.startsWith('image/')
      ),
      methodTanProcess: mtp
    });
  }, [
    fintsTestTanDecoupled,
    fintsTestTanProcessCode,
    fintsTestTanChallengeMediaB64,
    fintsTestTanChallengeMediaMime,
    fintsTestTanMethodOptions,
    fintsTestSelectedTanMethod
  ]);

  const testFintsConnection = async () => {
    if (user?.role !== 'admin') return;
    const form = document.getElementById('geldkonten-settings-form') as HTMLFormElement | null;
    if (!form || !paypalSettings) {
      setFintsTestResult({
        success: false,
        message: 'Einstellungen noch nicht geladen — bitte kurz warten.'
      });
      return;
    }

    setTestingFintsConnection(true);
    setFintsTestResult(null);
    setConnectionTestResult(null);
    clearFintsTestTanUi();
    try {
      const fd = new FormData(form);
      const body: Record<string, string | undefined> = {
        fints_url: String(fd.get('fints_url') || ''),
        fints_blz: String(fd.get('fints_blz') || ''),
        fints_user_id: String(fd.get('fints_user_id') || ''),
        fints_account_iban: String(fd.get('fints_account_iban') || ''),
        fints_product_id: String(fd.get('fints_product_id') || ''),
        fints_security_function: String(fd.get('fints_security_function') || ''),
        fints_tan_medium_name: String(fd.get('fints_tan_medium_name') || ''),
        fints_customer_id: String(fd.get('fints_customer_id') || '')
      };
      const pinTrim = fintsPin.trim();
      if (pinTrim) body.fints_pin = pinTrim;

      const response = await sessionFetch('/api/payments/giro/fints/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFintsTestResult({
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`
        });
        return;
      }

      if (data.success && data.status === 'tan_required') {
        const d = data.data || {};
        setFintsTestTanToken(d.tan_token || null);
        setFintsTestTanChallengeText(d.challenge_text || 'TAN erforderlich');
        setFintsTestTanChallengeMediaB64(d.challenge_media_base64 || null);
        setFintsTestTanChallengeMediaMime(d.challenge_media_mime || null);
        setFintsTestTanChallengeHhduc(d.challenge_hhduc || null);
        setFintsTestTanDecoupled(!!d.decoupled);
        applyFintsTestTanPayload(d as Record<string, unknown>);
        setFintsTestResult(null);
        return;
      }

      if (data.success && data.status === 'ok') {
        const d = data.data || {};
        setFintsTestResult({
          success: true,
          message: `Kontoauszug: ${d.transaction_count ?? 0} Buchung(en) (${d.from} – ${d.to}).`,
          transaction_count: d.transaction_count,
          transactions_preview: d.transactions_preview || [],
          from: d.from,
          to: d.to
        });
        return;
      }

      setFintsTestResult({
        success: false,
        message: data.message || 'Unerwartete Antwort vom Server'
      });
    } catch (error) {
      console.error('Error testing FinTS statement:', error);
      setFintsTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Fehler beim FinTS-Kontoauszug-Test'
      });
    } finally {
      setTestingFintsConnection(false);
    }
  };

  const submitFintsTestTan = async () => {
    if (user?.role !== 'admin') return;
    if (!fintsTestTanToken) {
      setFintsTestResult({ success: false, message: 'Keine aktive TAN-Sitzung für den Test.' });
      return;
    }
    if (!fintsTestTanDecoupled && !fintsTestTanValue.trim()) {
      setFintsTestResult({ success: false, message: 'Bitte TAN eingeben.' });
      return;
    }
    if (fintsTestTanMethodOptions.length > 0 && !String(fintsTestSelectedTanMethod || '').trim()) {
      setFintsTestResult({ success: false, message: 'Bitte TAN-Verfahren wählen.' });
      return;
    }

    setTestingFintsConnection(true);
    setFintsTestResult(null);
    try {
      const tanPayload: Record<string, string> = {
        tan_token: fintsTestTanToken,
        tan: fintsTestTanDecoupled ? '' : fintsTestTanValue.trim()
      };
      const sm = String(fintsTestSelectedTanMethod || '').trim();
      if (sm) tanPayload.fints_security_function = sm;

      const response = await sessionFetch('/api/payments/giro/fints/test-connection/tan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tanPayload)
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFintsTestResult({
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`
        });
        return;
      }

      if (data.success && data.status === 'tan_required') {
        const d = data.data || {};
        setFintsTestTanToken(d.tan_token || null);
        setFintsTestTanChallengeText(d.challenge_text || 'TAN erforderlich');
        setFintsTestTanChallengeMediaB64(d.challenge_media_base64 || null);
        setFintsTestTanChallengeMediaMime(d.challenge_media_mime || null);
        setFintsTestTanChallengeHhduc(d.challenge_hhduc || null);
        setFintsTestTanDecoupled(!!d.decoupled);
        applyFintsTestTanPayload(d as Record<string, unknown>);
        setFintsTestTanValue('');
        return;
      }

      if (data.success && data.status === 'ok') {
        clearFintsTestTanUi();
        const d = data.data || {};
        setFintsTestResult({
          success: true,
          message: `Kontoauszug: ${d.transaction_count ?? 0} Buchung(en) (${d.from} – ${d.to}).`,
          transaction_count: d.transaction_count,
          transactions_preview: d.transactions_preview || [],
          from: d.from,
          to: d.to
        });
        return;
      }

      setFintsTestResult({
        success: false,
        message: data.message || 'Unerwartete Antwort nach TAN'
      });
    } catch (error) {
      console.error('FinTS test TAN error:', error);
      setFintsTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'TAN-Verarbeitung fehlgeschlagen'
      });
    } finally {
      setTestingFintsConnection(false);
    }
  };

  const updatePersonalData = async (updatedData: Partial<PersonalData>) => {
    setSaveStatus('saving');
    try {
      const response = await sessionFetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        // Update local state
        setPersonalData(prev => prev ? { ...prev, ...updatedData } : null);
        setError(null);
        setSaveStatus('saved');
        
        // Reset save status after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
        
        return true;
      } else {
        throw new Error(data.error || 'Failed to update personal data');
      }
    } catch (error) {
      console.error('Error updating personal data:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Speichern der persönlichen Daten');
      setSaveStatus('error');
      
      // Reset error status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
      
      return false;
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
      fetchUserStats();
    }
  }, [activeTab, searchTerm, roleFilter]);

  useEffect(() => {
    if (activeTab === 'appearance') {
      setUiThemePreference(getThemePreference());
      setUiTextScale(getTextScale());
    } else if (activeTab === 'personal') {
      fetchPersonalData();
      fetchUserEmailCredentials();
      fetchNextcloudAdminStatus();
      fetchOnedriveAdminStatus();
      api.get('/storage/providers').then((res) => {
        if (res.data?.success) setStoragePublic(res.data.data);
      }).catch(() => setStoragePublic(null));
    } else if (activeTab === 'geldkonten' && isAdmin) {
      fetchPaypalSettings();
    } else if (activeTab === 'organization' && canManageSchoolSettings) {
      fetchEmailLabels();
      api.get('/settings/student-email-policy')
        .then(res => {
          if (res.data.success) {
            setStudentEmailMinAge(res.data.data.minAgeYears);
          }
        })
        .catch(err => {
          console.error('Error loading student email policy:', err);
        });
      api
        .get('/settings')
        .then(res => {
          if (res.data?.success && res.data?.data && typeof res.data.data === 'object') {
            const raw = (res.data.data as Record<string, unknown>)['students.hide_inactive_attic'];
            const s = String(raw ?? '')
              .trim()
              .toLowerCase();
            setHideInactiveStudentsFromAttic(
              s === '1' || s === 'true' || s === 'yes' || s === 'on'
            );
          }
        })
        .catch(err => {
          console.error('Error loading school visibility settings:', err);
        });
      if (isAdmin) {
        fetchQueuePermissions();
      }
    } else if (activeTab === 'system-admin' && isAdmin) {
      fetchEmailSystemSettings();
      fetchScheduledEmailTimezone();
      fetchLogLevel();
      fetchImageSearchSourceSettings();
      fetchOnlyofficeSettings();
      fetchStorageProvidersSettings();
      fetchNextcloudSettings();
      fetchOnedriveSettings();
      fetchLlmProfiles();
      fetchStudentFileBackfills();
      fetchBulkImportNameLlmSettings();
      fetchHubConnectionStatus();
      fetchTomAssistantMode();
      fetchSelfRegistrationSetting();
      fetchParentPortalSetting();
    } else if (activeTab === 'maintenance' && isAdmin) {
      fetchQueuePermissions();
    }
  }, [activeTab, canManageSchoolSettings, isAdmin]);

  useEffect(() => {
    if (activeTab !== 'geldkonten' || !isAdmin) {
      setFintsBankHits([]);
      setFintsBankSearchLoading(false);
      return undefined;
    }

    const q = fintsBankSearchQuery.trim();
    if (q.length < 2) {
      setFintsBankHits([]);
      setFintsBankSearchLoading(false);
      return undefined;
    }

    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      setFintsBankSearchLoading(true);
      try {
        const response = await sessionFetch(
          `/api/payments/settings/fints/banks/search?q=${encodeURIComponent(q)}&limit=30`,
          {
            signal: ctrl.signal
          }
        );
        let payload: { success?: boolean; data?: { banks?: FintsBankCatalogHit[] }; message?: string } = {};
        try {
          payload = await response.json();
        } catch {
          /* ignore */
        }
        if (!response.ok || !payload.success) {
          setFintsBankHits([]);
          return;
        }
        const banks = payload.data?.banks;
        setFintsBankHits(Array.isArray(banks) ? banks : []);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('FinTS bank search:', err);
        }
      } finally {
        setFintsBankSearchLoading(false);
      }
    }, 380);

    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [fintsBankSearchQuery, activeTab, isAdmin]);

  const applyFintsBankPick = (b: FintsBankCatalogHit) => {
    if (fintsUrlInputRef.current) fintsUrlInputRef.current.value = b.url;
    if (fintsBlzInputRef.current) fintsBlzInputRef.current.value = b.blz;
    setFintsBankHits([]);
    setFintsBankSearchQuery('');
    toast.success(`${b.name}: FinTS-URL und BLZ übernommen`);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'schulleitung': return 'bg-purple-100 text-purple-800';
      case 'abteilungsleiter': return 'bg-indigo-100 text-indigo-800';
      case 'klassenlehrer': return 'bg-cyan-100 text-cyan-800';
      case 'teacher': return 'bg-blue-100 text-blue-800';
      case 'student': return 'bg-green-100 text-green-800';
      case 'guest': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'schulleitung': return 'Schulleitung';
      case 'abteilungsleiter': return 'Abteilungsleiter';
      case 'klassenlehrer': return 'Klassenlehrer';
      case 'teacher': return 'Lehrer';
      case 'student': return 'Schüler';
      case 'guest': return 'Gast';
      default: return role;
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return '⚙️';
      case 'schulleitung': return '🏫';
      case 'abteilungsleiter': return '🏢';
      case 'klassenlehrer': return '📚';
      case 'teacher': return '👨‍🏫';
      case 'student': return '🎓';
      case 'guest': return '👤';
      default: return '❓';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  // Email Settings Functions
  const fetchEmailSystemSettings = async () => {
    if (user?.role !== 'admin') return;
    
    setEmailSystemLoading(true);
    try {
      const response = await api.get('/email/system-settings');
      if (response.data.success) {
        setEmailSystemSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching email system settings:', error);
    } finally {
      setEmailSystemLoading(false);
    }
  };

  const fetchScheduledEmailTimezone = async () => {
    setScheduledEmailTimezoneLoading(true);
    try {
      const response = await api.get('/settings/scheduled-email-timezone');
      if (response.data.success) {
        setScheduledEmailTimezone(response.data.data?.timezone ?? null);
      }
    } catch (error) {
      console.error('Error fetching scheduled email timezone:', error);
    } finally {
      setScheduledEmailTimezoneLoading(false);
    }
  };

  const saveScheduledEmailTimezone = async (value: string) => {
    setScheduledEmailTimezoneSaving(true);
    try {
      await api.put('/settings/scheduled-email-timezone', { value });
      setScheduledEmailTimezone(value);
      toast.success('Zeitzone für Später senden gespeichert');
    } catch (error: any) {
      console.error('Error saving scheduled email timezone:', error);
      toast.error(error.response?.data?.message || 'Fehler beim Speichern der Zeitzone');
    } finally {
      setScheduledEmailTimezoneSaving(false);
    }
  };

  const saveEmailSystemSettings = async (settings: EmailSystemSettings) => {
    if (user?.role !== 'admin') return;
    
    console.log('Saving email system settings:', settings);
    
    try {
      const response = await api.put('/email/system-settings', settings);
      console.log('API response:', response.data);
      
      if (response.data.success) {
        console.log('Email settings saved successfully');
        setEmailSystemSettings(settings);
        setSaveStatus('saved');
        toast.success('E-Mail-Systemeinstellungen erfolgreich gespeichert');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        console.error('API returned success: false', response.data);
        setSaveStatus('error');
        toast.error('Fehler beim Speichern der E-Mail-Einstellungen');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error: any) {
      console.error('Error saving email system settings:', error);
      console.error('Error response:', error.response?.data);
      setSaveStatus('error');
      toast.error(`Fehler beim Speichern: ${error.response?.data?.details || error.message || 'Unbekannter Fehler'}`);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const fetchUserEmailCredentials = async () => {
    setUserEmailLoading(true);
    try {
      const response = await api.get('/email/credentials');
      if (response.data.success) {
        setUserEmailCredentials(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user email credentials:', error);
    } finally {
      setUserEmailLoading(false);
    }
  };

  const saveUserEmailCredentials = async (credentials: UserEmailCredentials & { email_password?: string }) => {
    try {
      if (credentials.id) {
        // Update existing
        const response = await api.put(`/email/credentials/${credentials.id}`, credentials);
        if (response.data.success) {
          await fetchUserEmailCredentials();
          setShowEmailCredentialModal(false);
          setEditingEmailCredential(null);
          toast.success('E-Mail-Konto erfolgreich aktualisiert');
        }
      } else {
        // Create new
        const response = await api.post('/email/credentials', credentials);
        if (response.data.success) {
          await fetchUserEmailCredentials();
          setShowEmailCredentialModal(false);
          setEditingEmailCredential(null);
          toast.success('E-Mail-Konto erfolgreich hinzugefügt');
        }
      }
    } catch (error: any) {
      console.error('Error saving email credentials:', error);
      toast.error(`Fehler beim Speichern des E-Mail-Kontos: ${error.response?.data?.error || error.message}`);
    }
  };

  const deleteUserEmailCredentials = async (id: number) => {
    try {
      const response = await api.delete(`/email/credentials/${id}`);
      if (response.data.success) {
        await fetchUserEmailCredentials();
        toast.success('E-Mail-Konto erfolgreich gelöscht');
      }
    } catch (error: any) {
      console.error('Error deleting email credentials:', error);
      toast.error(`Fehler beim Löschen des E-Mail-Kontos: ${error.response?.data?.error || error.message}`);
    }
  };

  // Queue Permissions Functions
  const fetchQueuePermissions = async () => {
    // Only fetch if user is admin (backend will also check, but we can skip the request if not admin)
    if (!user || user.role !== 'admin') {
      console.log('Skipping queue permissions fetch - user is not admin');
      return;
    }
    
    // Backend route already has requireAdmin middleware, so we can try to fetch
    // If user is not admin, backend will return 403 and we handle it gracefully
    setQueuePermissionsLoading(true);
    try {
      const response = await api.get('/settings/queue-permissions');
      console.log('Queue permissions response:', response.data);
      if (response.data.success) {
        setQueuePermissions(response.data.data.permissions || []);
        setQueueAvailableRoles(response.data.data.availableRoles || []);
        // Filter out students on frontend as well (100% guarantee)
        const users = (response.data.data.availableUsers || []).filter((user: any) => {
          const role = (user.role || '').toLowerCase();
          return role !== 'student' && !role.includes('student');
        });
        setQueueAvailableUsers(users);
        console.log('Loaded roles:', response.data.data.availableRoles?.length || 0);
        console.log('Loaded users (students excluded):', users.length);
        console.log('Loaded permissions:', response.data.data.permissions?.length || 0);
      }
    } catch (error: any) {
      // Silently handle 403 errors (user is not admin)
      if (error.response?.status === 403) {
        console.log('User does not have admin access to queue permissions');
        return;
      }
      console.error('Error fetching queue permissions:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Fehler beim Laden der Queue-Berechtigungen: ${error.response?.data?.error || error.message}`);
    } finally {
      setQueuePermissionsLoading(false);
    }
  };

  // Filter users based on search term
  const filteredUsersForSearch = useMemo(() => {
    if (!userSearchTerm || userSearchTerm.trim() === '') {
      return queueAvailableUsers.slice(0, 10); // Show first 10 when no search
    }
    
    const search = userSearchTerm.toLowerCase().trim();
    return queueAvailableUsers.filter((user: any) => {
      const firstName = (user.first_name || '').toLowerCase();
      const lastName = (user.last_name || '').toLowerCase();
      const username = (user.username || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      
      return (
        firstName.includes(search) ||
        lastName.includes(search) ||
        username.includes(search) ||
        `${firstName} ${lastName}`.includes(search) ||
        getRoleDisplayName(user.role).toLowerCase().includes(search)
      );
    }).slice(0, 10); // Limit to 10 results
  }, [queueAvailableUsers, userSearchTerm]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowUserSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const addQueuePermission = async () => {
    if (!newPermissionValue.trim()) {
      toast.error('Bitte wählen Sie eine Rolle oder einen Benutzer aus');
      return;
    }

    try {
      await api.post('/settings/queue-permissions', {
        permission_type: newPermissionType,
        permission_value: newPermissionValue
      });
      toast.success('Berechtigung hinzugefügt');
      setNewPermissionValue('');
      setUserSearchTerm('');
      setShowUserSearchResults(false);
      fetchQueuePermissions();
    } catch (error: any) {
      console.error('Error adding queue permission:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Hinzufügen der Berechtigung');
    }
  };

  const removeQueuePermission = async (permissionId: number) => {
    if (!window.confirm('Möchten Sie diese Berechtigung wirklich entfernen?')) {
      return;
    }

    try {
      await api.delete(`/settings/queue-permissions/${permissionId}`);
      toast.success('Berechtigung entfernt');
      fetchQueuePermissions();
    } catch (error: any) {
      console.error('Error removing queue permission:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Entfernen der Berechtigung');
    }
  };

  const fetchLogLevel = async () => {
    if (!user || user.role !== 'admin') return;
    setLogLevelLoading(true);
    try {
      const res = await api.get('/settings/log-level');
      if (res.data?.success && res.data?.data) {
        setLogLevel(res.data.data.level || 'info');
        if (Array.isArray(res.data.data.allowedLevels)) {
          setLogLevelAllowedLevels(res.data.data.allowedLevels);
        }
      }
      const availabilityRes = await api.get('/settings/llm-model-availability');
      if (availabilityRes.data?.success && availabilityRes.data?.data) {
        setLlmAvailability(availabilityRes.data.data);
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden des Log-Levels');
      }
    } finally {
      setLogLevelLoading(false);
    }
  };

  const isModelAvailable = (model: string) => {
    const row = llmAvailability?.models?.find((m) => m.model === model);
    if (!row) return true;
    return row.available !== false;
  };

  const saveLogLevel = async (level: string) => {
    setLogLevelSaving(true);
    try {
      await api.put('/settings/log-level', { level });
      setLogLevel(level);
      toast.success('Log-Level wurde aktualisiert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern des Log-Levels');
    } finally {
      setLogLevelSaving(false);
    }
  };

  const fetchImageSearchSourceSettings = async () => {
    if (!user || user.role !== 'admin') return;
    setImageSearchSourceSettingsLoading(true);
    try {
      const res = await api.get('/settings/image-search-sources');
      if (res.data?.success && res.data?.data) {
        setImageSearchSourceSettings({
          wikipedia: !!res.data.data.wikipedia,
          pixabay: !!res.data.data.pixabay,
          pexels: !!res.data.data.pexels,
          unsplash: !!res.data.data.unsplash,
          google: !!res.data.data.google
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Bildquellen-Einstellungen');
      }
    } finally {
      setImageSearchSourceSettingsLoading(false);
    }
  };

  const saveImageSearchSourceSettings = async (nextSettings: ImageSearchSourceSettings) => {
    setImageSearchSourceSettingsSaving(true);
    try {
      await api.put('/settings/image-search-sources', nextSettings);
      setImageSearchSourceSettings(nextSettings);
      toast.success('Bildquellen-Einstellungen gespeichert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der Bildquellen-Einstellungen');
    } finally {
      setImageSearchSourceSettingsSaving(false);
    }
  };

  const fetchTomAssistantMode = async () => {
    if (!user || user.role !== 'admin') return;
    setTomAssistantModeLoading(true);
    try {
      const res = await api.get('/settings/tom-assistant-mode');
      const mode = res.data?.data?.mode;
      if (mode === 'text' || mode === 'voice') {
        setTomAssistantMode(mode);
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Tom-Einstellung');
      }
    } finally {
      setTomAssistantModeLoading(false);
    }
  };

  const saveTomAssistantMode = async (mode: TomAssistantMode) => {
    setTomAssistantModeSaving(true);
    try {
      await api.put('/settings/tom-assistant-mode', { mode });
      setTomAssistantMode(mode);
      dispatchTomAssistantModeUpdated(mode);
      toast.success(
        mode === 'text'
          ? 'Tom: Textmodus für alle Nutzer aktiviert'
          : 'Tom: Sprachmodus für alle Nutzer aktiviert'
      );
    } catch (err: any) {
      const details = err.response?.data?.details;
      const detailMsg =
        Array.isArray(details) && details[0]?.msg ? String(details[0].msg) : null;
      toast.error(
        detailMsg || err.response?.data?.message || err.response?.data?.error || 'Fehler beim Speichern der Tom-Einstellung'
      );
    } finally {
      setTomAssistantModeSaving(false);
    }
  };

  const fetchSelfRegistrationSetting = async () => {
    if (!user || user.role !== 'admin') return;
    setSelfRegistrationLoading(true);
    try {
      const res = await api.get('/settings/self-registration');
      setSelfRegistrationEnabled(Boolean(res.data?.data?.enabled));
      setSelfRegistrationUpdatedBy(res.data?.data?.updatedBy || null);
      setSelfRegistrationUpdatedAt(res.data?.data?.updatedAt || null);
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Selbstregistrierungseinstellung');
      }
    } finally {
      setSelfRegistrationLoading(false);
    }
  };

  const saveSelfRegistrationSetting = async (enabled: boolean) => {
    setSelfRegistrationSaving(true);
    try {
      const res = await api.put('/settings/self-registration', { enabled });
      setSelfRegistrationEnabled(Boolean(res.data?.data?.enabled));
      setSelfRegistrationUpdatedBy(res.data?.data?.updatedBy || null);
      setSelfRegistrationUpdatedAt(res.data?.data?.updatedAt || null);
      toast.success(
        enabled
          ? 'Selbstregistrierung aktiviert'
          : 'Selbstregistrierung deaktiviert'
      );
    } catch (err: any) {
      const details = err.response?.data?.details;
      const detailMsg =
        Array.isArray(details) && details[0]?.msg ? String(details[0].msg) : null;
      toast.error(
        detailMsg || err.response?.data?.message || err.response?.data?.error || 'Fehler beim Speichern der Selbstregistrierung'
      );
    } finally {
      setSelfRegistrationSaving(false);
    }
  };

  const fetchParentPortalSetting = async () => {
    if (!user || user.role !== 'admin') return;
    setParentPortalLoading(true);
    try {
      const res = await api.get('/settings/parent-portal');
      setParentPortalEnabled(Boolean(res.data?.data?.enabled));
      setParentPortalUpdatedBy(res.data?.data?.updatedBy || null);
      setParentPortalUpdatedAt(res.data?.data?.updatedAt || null);
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Elternportal-Einstellung');
      }
    } finally {
      setParentPortalLoading(false);
    }
  };

  const saveParentPortalSetting = async (enabled: boolean) => {
    setParentPortalSaving(true);
    try {
      const res = await api.put('/settings/parent-portal', { enabled });
      setParentPortalEnabled(Boolean(res.data?.data?.enabled));
      setParentPortalUpdatedBy(res.data?.data?.updatedBy || null);
      setParentPortalUpdatedAt(res.data?.data?.updatedAt || null);
      toast.success(
        enabled ? 'Elternportal aktiviert' : 'Elternportal deaktiviert'
      );
    } catch (err: any) {
      const details = err.response?.data?.details;
      const detailMsg =
        Array.isArray(details) && details[0]?.msg ? String(details[0].msg) : null;
      toast.error(
        detailMsg ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Fehler beim Speichern der Elternportal-Einstellung'
      );
    } finally {
      setParentPortalSaving(false);
    }
  };

  const fetchOnlyofficeSettings = async () => {
    if (!user || user.role !== 'admin') return;
    setOnlyofficeLoading(true);
    try {
      const res = await api.get('/settings/onlyoffice');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setOnlyofficeSettings({
          docsUrl: d.docsUrl ?? '',
          secretKey: d.secretKey ?? '',
          disableSslVerify: !!d.disableSslVerify,
          internalUrl: d.internalUrl ?? '',
          callbackBaseUrl: d.callbackBaseUrl ?? ''
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der OnlyOffice-Einstellungen');
      }
    } finally {
      setOnlyofficeLoading(false);
    }
  };

  const saveOnlyofficeSettings = async () => {
    setOnlyofficeSaving(true);
    try {
      await api.put('/settings/onlyoffice', {
        docsUrl: onlyofficeSettings.docsUrl.trim(),
        secretKey: onlyofficeSettings.secretKey,
        disableSslVerify: onlyofficeSettings.disableSslVerify,
        internalUrl: onlyofficeSettings.internalUrl.trim(),
        callbackBaseUrl: onlyofficeSettings.callbackBaseUrl.trim()
      });
      toast.success('OnlyOffice-Einstellungen gespeichert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der OnlyOffice-Einstellungen');
    } finally {
      setOnlyofficeSaving(false);
    }
  };

  const mapStorageProvidersFromApi = (d: Record<string, unknown>): StorageProvidersSettings => ({
    lernraketeEnabled: d.lernraketeEnabled !== false,
    nextcloudEnabled: !!d.nextcloudEnabled,
    onedriveEnabled: !!d.onedriveEnabled,
    nextcloudUseLernraketeCredentials: !!d.nextcloudUseLernraketeCredentials
  });

  const fetchStorageProvidersSettings = async () => {
    if (!isAdmin) return;
    const fetchId = ++storageProvidersFetchId.current;
    setStorageProvidersLoading(true);
    try {
      const res = await api.get('/settings/storage-providers');
      if (fetchId !== storageProvidersFetchId.current) return;
      if (res.data?.success && res.data?.data) {
        setStorageProviders(mapStorageProvidersFromApi(res.data.data));
      }
    } catch {
      if (fetchId === storageProvidersFetchId.current) {
        toast.error('Fehler beim Laden der Dateispeicher-Einstellungen');
      }
    } finally {
      if (fetchId === storageProvidersFetchId.current) {
        setStorageProvidersLoading(false);
      }
    }
  };

  const saveStorageProvidersSettings = (
    payload?: StorageProvidersSettings,
    options?: { silent?: boolean }
  ) => {
    const toSave = payload ?? storageProviders;
    const silent = options?.silent ?? false;
    storageProvidersSaveQueue.current = storageProvidersSaveQueue.current
      .catch(() => undefined)
      .then(async () => {
        storageProvidersFetchId.current += 1;
        setStorageProvidersSaving(true);
        try {
          const res = await api.put('/settings/storage-providers', toSave);
          if (res.data?.success && res.data?.data) {
            setStorageProviders(mapStorageProvidersFromApi(res.data.data));
            if (!silent) {
              toast.success('Dateispeicher-Einstellungen gespeichert');
            }
          } else {
            toast.error(res.data?.error || 'Fehler beim Speichern der Dateispeicher-Einstellungen');
          }
        } catch (err: any) {
          const details = err.response?.data?.details;
          const detailMsg =
            Array.isArray(details) && details[0]?.msg ? String(details[0].msg) : null;
          toast.error(
            detailMsg || err.response?.data?.error || 'Fehler beim Speichern der Dateispeicher-Einstellungen'
          );
        } finally {
          setStorageProvidersSaving(false);
        }
      });
    return storageProvidersSaveQueue.current;
  };

  const updateStorageProvider = (patch: Partial<StorageProvidersSettings>) => {
    const next = { ...storageProviders, ...patch };
    setStorageProviders(next);
    void saveStorageProvidersSettings(next, { silent: true });
  };

  const fetchNextcloudSettings = async () => {
    if (!user || user.role !== 'admin') return;
    setNextcloudLoading(true);
    try {
      const res = await api.get('/settings/nextcloud');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setNextcloudSettings({
          baseUrl: d.baseUrl ?? '',
          customServerUrl: d.customServerUrl ?? d.baseUrl ?? '',
          accountMode: d.accountMode ?? 'school_and_personal',
          disableSslVerify: !!d.disableSslVerify,
          useLernraketeCredentials: !!d.useLernraketeCredentials
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Nextcloud-Einstellungen');
      }
    } finally {
      setNextcloudLoading(false);
    }
  };

  const saveNextcloudSettings = async () => {
    setNextcloudSaving(true);
    try {
      await api.put('/settings/nextcloud', {
        baseUrl: nextcloudSettings.baseUrl.trim(),
        customServerUrl: nextcloudSettings.customServerUrl.trim(),
        accountMode: nextcloudSettings.accountMode,
        disableSslVerify: nextcloudSettings.disableSslVerify,
        useLernraketeCredentials: nextcloudSettings.useLernraketeCredentials
      });
      setStorageProviders(s => ({ ...s, nextcloudUseLernraketeCredentials: nextcloudSettings.useLernraketeCredentials }));
      toast.success('Nextcloud-Einstellungen gespeichert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der Nextcloud-Einstellungen');
    } finally {
      setNextcloudSaving(false);
    }
  };

  const fetchNextcloudAdminStatus = async () => {
    try {
      const res = await api.get('/nextcloud/status');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setNextcloudAdminConfigured(!!d.enabled && !!d.adminConfigured);
      }
    } catch {
      setNextcloudAdminConfigured(false);
    }
  };

  const fetchOnedriveAdminStatus = async () => {
    try {
      const res = await api.get('/onedrive/status');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setOnedriveAdminConfigured(!!d.enabled && !!d.adminConfigured);
      }
    } catch {
      setOnedriveAdminConfigured(false);
    }
  };

  const fetchOnedriveSettings = async () => {
    if (!user || user.role !== 'admin') return;
    setOnedriveLoading(true);
    try {
      const res = await api.get('/settings/onedrive');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setOnedriveSettings({
          clientId: d.clientId ?? '',
          clientSecret: '',
          accountMode: d.accountMode ?? 'school_and_personal',
          customTenantId: d.customTenantId ?? '',
          redirectUri: d.redirectUri ?? ''
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der OneDrive-Einstellungen');
      }
    } finally {
      setOnedriveLoading(false);
    }
  };

  const saveOnedriveSettings = async () => {
    setOnedriveSaving(true);
    try {
      const body = {
        clientId: onedriveSettings.clientId.trim(),
        clientSecret: onedriveSettings.clientSecret,
        accountMode: onedriveSettings.accountMode,
        customTenantId: onedriveSettings.customTenantId.trim(),
        redirectUri: onedriveSettings.redirectUri.trim()
      };
      await api.put('/settings/onedrive', body);
      toast.success('OneDrive-Einstellungen gespeichert');
      setOnedriveSettings(s => ({ ...s, clientSecret: '' }));
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der OneDrive-Einstellungen');
    } finally {
      setOnedriveSaving(false);
    }
  };

  const fetchLlmProfiles = async () => {
    if (!user || user.role !== 'admin') return;
    setLlmProfilesLoading(true);
    try {
      const res = await api.get('/settings/llm-model-profiles');
      if (res.data?.success && res.data?.data) {
        if (Array.isArray(res.data.data.profiles)) {
          setLlmProfiles(res.data.data.profiles);
        }
        if (Array.isArray(res.data.data.allowedModels) && res.data.data.allowedModels.length > 0) {
          setLlmAllowedModels(res.data.data.allowedModels);
        }
      }
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der LLM-Modellprofile');
      }
    } finally {
      setLlmProfilesLoading(false);
    }
  };

  const saveLlmProfiles = async (profiles: LlmModelProfile[]) => {
    setLlmProfilesSaving(true);
    try {
      const res = await api.put('/settings/llm-model-profiles', { profiles });
      setLlmProfiles(res.data?.data?.profiles || profiles);
      toast.success('LLM-Modellprofile gespeichert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der LLM-Modellprofile');
    } finally {
      setLlmProfilesSaving(false);
    }
  };

  const fetchStudentFileBackfills = async () => {
    if (!user || user.role !== 'admin') return;
    setStudentFileBackfillsLoading(true);
    try {
      const res = await api.get('/student-files/processing/backfill', { params: { limit: 6 } });
      setStudentFileBackfills(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden des Akten-Backfills');
      }
    } finally {
      setStudentFileBackfillsLoading(false);
    }
  };

  const startStudentFileBackfillRun = async (force = false) => {
    setStudentFileBackfillsStarting(true);
    try {
      await api.post('/student-files/processing/backfill', { batchSize: 100, force });
      toast.success(force ? 'Akten-Reprocessing gestartet' : 'Akten-Backfill gestartet');
      fetchStudentFileBackfills();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Starten des Akten-Backfills');
    } finally {
      setStudentFileBackfillsStarting(false);
    }
  };

  const fetchBulkImportNameLlmSettings = async () => {
    if (!user || user.role !== 'admin') return;
    setBulkImportNameLlmLoading(true);
    try {
      const res = await api.get('/settings');
      const all = res.data?.success ? (res.data.data || {}) : {};
      const enabledRaw =
        all['classwork.batch_import.name_llm.enabled'] ?? all['classwork.bulk_import.name_llm.enabled'];
      const modelRaw =
        all['classwork.batch_import.name_llm.model'] ?? all['classwork.bulk_import.name_llm.model'];
      const enabled = ['1', 'true', 'yes', 'on'].includes(String(enabledRaw || '').trim().toLowerCase());
      const model = String(modelRaw || 'gpt-4o-mini').trim() || 'gpt-4o-mini';
      setBulkImportNameLlmSettings({ enabled, model });
    } catch (err: any) {
      if (err.response?.status !== 403) {
        toast.error('Fehler beim Laden der Sammel-PDF LLM-Einstellungen');
      }
    } finally {
      setBulkImportNameLlmLoading(false);
    }
  };

  const fetchHubConnectionStatus = async () => {
    if (user?.role !== 'admin') return;
    setHubConnectionLoading(true);
    try {
      const res = await api.get('/hub/status');
      if (res.data?.success && res.data?.hub) {
        setHubConnection(res.data.hub as HubConnectionInfo);
      } else {
        setHubConnection(null);
      }
    } catch (err: any) {
      console.error('Hub status:', err);
      setHubConnection(null);
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.error || 'Hub-Status konnte nicht geladen werden');
      }
    } finally {
      setHubConnectionLoading(false);
    }
  };

  const saveBulkImportNameLlmSettings = async (next: BulkImportNameLlmSettings) => {
    setBulkImportNameLlmSaving(true);
    try {
      await Promise.all([
        api.put(`/settings/${encodeURIComponent('classwork.batch_import.name_llm.enabled')}`, {
          value: next.enabled ? '1' : '0'
        }),
        api.put(`/settings/${encodeURIComponent('classwork.batch_import.name_llm.model')}`, {
          value: String(next.model || 'gpt-4o-mini').trim() || 'gpt-4o-mini'
        }),
        api.put(`/settings/${encodeURIComponent('classwork.bulk_import.name_llm.enabled')}`, {
          value: next.enabled ? '1' : '0'
        }),
        api.put(`/settings/${encodeURIComponent('classwork.bulk_import.name_llm.model')}`, {
          value: String(next.model || 'gpt-4o-mini').trim() || 'gpt-4o-mini'
        })
      ]);
      setBulkImportNameLlmSettings(next);
      toast.success('Sammel-PDF LLM-Einstellungen gespeichert');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern der Sammel-PDF LLM-Einstellungen');
    } finally {
      setBulkImportNameLlmSaving(false);
    }
  };

  // Email Labels Functions
  const fetchEmailLabels = async () => {
    if (user?.role !== 'admin') return;
    
    setEmailLabelsLoading(true);
    try {
      const response = await api.get('/email-client-advanced/labels');
      if (response.data.success) {
        setEmailLabels(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching email labels:', error);
      toast.error(`Fehler beim Laden der Labels: ${error.response?.data?.error || error.message}`);
    } finally {
      setEmailLabelsLoading(false);
    }
  };

  const saveEmailLabel = async (label: any) => {
    try {
      if (label.id) {
        // Update existing label
        await api.put(`/email-client-advanced/labels/${label.id}`, {
          name: label.name,
          description: label.description,
          color: label.color,
          icon: label.icon,
          llm_criteria: label.llm_criteria
        });
        toast.success('Label aktualisiert');
      } else {
        // Create new label
        await api.post('/email-client-advanced/labels', {
          name: label.name,
          description: label.description,
          color: label.color,
          icon: label.icon,
          llm_criteria: label.llm_criteria
        });
        toast.success('Label erstellt');
      }
      setEditingLabel(null);
      setNewLabel({
        name: '',
        description: '',
        color: '#6B7280',
        icon: 'tag',
        llm_criteria: {
          keywords: [],
          patterns: [],
          context_rules: [],
          priority: 'medium'
        }
      });
      setNewKeyword('');
      setNewPattern('');
      fetchEmailLabels();
    } catch (error: any) {
      console.error('Error saving email label:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Speichern des Labels';
      toast.error(errorMessage);
    }
  };

  const deleteEmailLabel = async (labelId: number) => {
    if (!confirm('Möchten Sie dieses Label wirklich löschen? Alle Zuweisungen werden ebenfalls entfernt.')) {
      return;
    }
    
    try {
      await api.delete(`/email-client-advanced/labels/${labelId}`);
      toast.success('Label gelöscht');
      fetchEmailLabels();
    } catch (error: any) {
      console.error('Error deleting email label:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Labels');
    }
  };

  const testEmailConnection = async (emailAddress?: string) => {
    const testKey = emailAddress || 'default';
    setTestingEmails(prev => new Set(prev).add(testKey));
    
    try {
      const response = await api.post('/email/test-connection', { email_address: emailAddress });
      setEmailTestResults(prev => ({ ...prev, [testKey]: response.data }));
      
      if (response.data.success) {
        toast.success('E-Mail-Verbindung erfolgreich getestet');
      } else {
        toast.error(`E-Mail-Verbindungstest fehlgeschlagen: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error testing email connection:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Test failed';
      setEmailTestResults(prev => ({ ...prev, [testKey]: { success: false, message: errorMessage } }));
      toast.error(`E-Mail-Verbindungstest fehlgeschlagen: ${errorMessage}`);
    } finally {
      setTestingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(testKey);
        return newSet;
      });
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Konfigurieren Sie Systemeinstellungen und verwalten Sie Benutzer
        </p>
      </div>

      <SettingsNavigation
        areas={SETTINGS_AREAS}
        tabs={tabs}
        activeArea={activeArea}
        activeTab={activeTab}
        onAreaChange={handleAreaChange}
        onTabChange={handleTabChange}
      />

      {(activeTab === 'organization' || activeTab === 'system-admin') && (
        <div className="space-y-6">
          {/* Email System Settings (Admin only) */}
          {activeTab === 'system-admin' && isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Server className="mr-2 h-5 w-5" />
                  E-Mail Systemeinstellungen
                </h2>
                
                {/* Save Status Indicator */}
                <div className="flex items-center space-x-2">
                  {saveStatus === 'saving' && (
                    <div className="flex items-center text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Speichern...
                    </div>
                  )}
                  {saveStatus === 'saved' && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Gespeichert
                    </div>
                  )}
                  {saveStatus === 'error' && (
                    <div className="flex items-center text-red-600">
                      <XCircle className="h-4 w-4 mr-2" />
                      Fehler beim Speichern
                    </div>
                  )}
                </div>
              </div>

              {emailSystemLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Lade E-Mail-Einstellungen...</p>
                </div>
              ) : (
                <EmailSystemSettingsForm
                  settings={emailSystemSettings}
                  onSave={saveEmailSystemSettings}
                  setSaveStatus={setSaveStatus}
                />
              )}
            </div>
          )}

          {/* Lernrakete Hub (Admin) */}
          {activeTab === 'system-admin' && isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Cloud className="mr-2 h-5 w-5 text-sky-600" />
                  Lernrakete Hub
                </h2>
                <button
                  type="button"
                  onClick={() => fetchHubConnectionStatus()}
                  disabled={hubConnectionLoading}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${hubConnectionLoading ? 'animate-spin' : ''}`} />
                  Verbindung prüfen
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Zentrale LLM-/Lizenz-Anbindung. Die Werte kommen aus der Server-Konfiguration (
                <code className="text-xs bg-gray-100 px-1 rounded">HUB_*</code> in der Backend-
                <code className="text-xs bg-gray-100 px-1 rounded">.env</code>
                ).                 Mit <code className="text-xs bg-gray-100 px-1 rounded">LLM_MODE=hub</code> laufen
                unterstützte Text-LLM-Jobs über den Hub; mit{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">LLM_MODE=openai</code> direkt über
                OpenAI. Bei gesetztem <code className="text-xs bg-gray-100 px-1 rounded">HUB_BASE_URL</code>{' '}
                pingt das Backend etwa minütlich (verbundene Clients im Hub-Admin).
              </p>
              {hubConnectionLoading && !hubConnection ? (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sky-600 mr-2" />
                  Lade Hub-Status…
                </div>
              ) : hubConnection ? (
                <div className="space-y-4">
                  <div
                    className={`rounded-md border px-3 py-2 text-sm ${
                      !hubConnection.configured
                        ? 'border-gray-200 bg-gray-50 text-gray-700'
                        : hubConnection.reachable
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                          : 'border-amber-200 bg-amber-50 text-amber-900'
                    }`}
                  >
                    <span className="font-medium">Status: </span>
                    {!hubConnection.configured && (hubConnection.message || 'Hub nicht konfiguriert (HUB_BASE_URL fehlt)')}
                    {hubConnection.configured && hubConnection.reachable && 'Verbunden – Hub-Core antwortet.'}
                    {hubConnection.configured && !hubConnection.reachable && (
                      <>
                        Konfiguriert, aber nicht erreichbar.
                        {hubConnection.error ? ` (${hubConnection.error})` : ''}
                      </>
                    )}
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Hub-URL</dt>
                      <dd className="font-mono text-gray-900 break-all">{hubConnection.baseUrl || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Mandant (Tenant-ID)</dt>
                      <dd className="font-mono text-gray-900">{hubConnection.tenantId || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">LLM-Modus (.env)</dt>
                      <dd className="font-mono text-gray-900">{hubConnection.llmMode ?? '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Text-LLM über Hub</dt>
                      <dd className="text-gray-900">
                        {(hubConnection.llmViaHub ?? hubConnection.useForAssistant) ? 'Ja' : 'Nein'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Hub-API-Key gesetzt</dt>
                      <dd className="text-gray-900">{hubConnection.apiKeyConfigured ? 'Ja' : 'Nein'}</dd>
                    </div>
                    {hubConnection.assistantModelHint ? (
                      <div>
                        <dt className="text-gray-500">Modell-Hinweis (Tom/Hub)</dt>
                        <dd className="font-mono text-gray-900">{hubConnection.assistantModelHint}</dd>
                      </div>
                    ) : null}
                    {hubConnection.assistantPriority ? (
                      <div>
                        <dt className="text-gray-500">Prioritätsklasse</dt>
                        <dd className="font-mono text-gray-900">{hubConnection.assistantPriority}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {hubConnection.modelPolicyError && (
                    <p className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1">
                      Modellrichtlinie vom Hub nicht lesbar: {hubConnection.modelPolicyError}
                    </p>
                  )}
                  {hubConnection.modelPolicy && (
                    <div className="text-xs border-t border-gray-100 pt-3">
                      <div className="font-medium text-gray-700 mb-1">
                        Modell- &amp; TTS-Richtlinie (Hub-Core)
                      </div>
                      <pre className="bg-gray-50 p-2 rounded overflow-x-auto max-h-56 text-[11px] leading-snug">
                        {JSON.stringify(hubConnection.modelPolicy, null, 2)}
                      </pre>
                    </div>
                  )}
                  {hubConnection.ops?.queueLag && (
                    <div className="text-xs text-gray-600 border-t border-gray-100 pt-3">
                      <span className="font-medium text-gray-700">Warteschlangen (Hub): </span>
                      live {hubConnection.ops.queueLag.unterricht_live ?? '—'}, normal{' '}
                      {hubConnection.ops.queueLag.normal ?? '—'}, batch {hubConnection.ops.queueLag.batch ?? '—'}
                      {typeof hubConnection.ops.providerLatencyP95Ms === 'number' && (
                        <>
                          {' '}
                          · p95 Latenz (simuliert){' '}
                          {hubConnection.ops.providerLatencyP95Ms} ms
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Keine Daten. „Verbindung prüfen“ wählen.</p>
              )}
            </div>
          )}

          {activeTab === 'system-admin' && isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                <Bot className="mr-2 h-5 w-5 text-blue-600" />
                Tom – Assistentenmodus
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Schulweite Voreinstellung für alle Lehrkräfte. Im Textmodus entfällt Spracherkennung (STT) und
                Sprachausgabe (TTS); Tom nutzt dieselbe KI und denselben Seitenkontext, die Oberfläche öffnet sich
                in einem Dialog.
              </p>
              {tomAssistantModeLoading ? (
                <p className="text-sm text-gray-500">Lade Einstellung…</p>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between max-w-xl">
                  <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                    <button
                      type="button"
                      disabled={tomAssistantModeSaving}
                      onClick={() => {
                        if (tomAssistantMode !== 'voice') void saveTomAssistantMode('voice');
                      }}
                      className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        tomAssistantMode === 'voice'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Mic className="h-4 w-4" />
                      Sprache
                    </button>
                    <button
                      type="button"
                      disabled={tomAssistantModeSaving}
                      onClick={() => {
                        if (tomAssistantMode !== 'text') void saveTomAssistantMode('text');
                      }}
                      className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        tomAssistantMode === 'text'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Text
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 sm:text-right">
                    {tomAssistantMode === 'voice'
                      ? 'Mikrofon in der Kopfleiste, Antworten per TTS'
                      : 'Tom-Button öffnet Chat-Dialog, nur Text'}
                    {tomAssistantModeSaving ? ' · Speichern…' : ''}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'system-admin' && isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                <Users className="mr-2 h-5 w-5 text-blue-600" />
                Selbstregistrierung Lehrkräfte
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Steuert, ob die Selbstregistrierung über die Login-Seite verfügbar ist. Standard ist deaktiviert.
              </p>
              {selfRegistrationLoading ? (
                <p className="text-sm text-gray-500">Lade Einstellung…</p>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between max-w-xl">
                  <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                    <Button
                      type="button"
                      size="sm"
                      disabled={selfRegistrationSaving}
                      onClick={() => {
                        if (!selfRegistrationEnabled) void saveSelfRegistrationSetting(true);
                      }}
                      variant={selfRegistrationEnabled ? 'primary' : 'outline'}
                      className="rounded-md"
                    >
                      Aktiviert
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={selfRegistrationSaving}
                      onClick={() => {
                        if (selfRegistrationEnabled) void saveSelfRegistrationSetting(false);
                      }}
                      variant={!selfRegistrationEnabled ? 'primary' : 'outline'}
                      className="rounded-md"
                    >
                      Deaktiviert
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 sm:text-right">
                    {selfRegistrationEnabled
                      ? 'Registrierungslink wird auf der Login-Seite angezeigt'
                      : 'Registrierungslink ist ausgeblendet'}
                    {selfRegistrationSaving ? ' · Speichern…' : ''}
                    {selfRegistrationUpdatedAt && (
                      <>
                        <br />
                        {`Zuletzt geändert: ${new Date(selfRegistrationUpdatedAt).toLocaleString('de-DE')}`}
                        {selfRegistrationUpdatedBy ? ` von ${selfRegistrationUpdatedBy}` : ''}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'system-admin' && isAdmin && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                <Users className="mr-2 h-5 w-5 text-cyan-600" />
                Elternportal
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Steuert, ob sich Eltern anmelden und das Elternfrontend nutzen können. Elternbriefe
                mit persönlichem Link funktionieren unabhängig davon weiter.
              </p>
              {parentPortalLoading ? (
                <p className="text-sm text-gray-500">Lade Einstellung…</p>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between max-w-xl">
                  <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                    <Button
                      type="button"
                      size="sm"
                      disabled={parentPortalSaving}
                      onClick={() => {
                        if (!parentPortalEnabled) void saveParentPortalSetting(true);
                      }}
                      variant={parentPortalEnabled ? 'primary' : 'outline'}
                      className="rounded-md"
                    >
                      Aktiviert
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={parentPortalSaving}
                      onClick={() => {
                        if (parentPortalEnabled) void saveParentPortalSetting(false);
                      }}
                      variant={!parentPortalEnabled ? 'primary' : 'outline'}
                      className="rounded-md"
                    >
                      Deaktiviert
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-right">
                    {parentPortalEnabled
                      ? 'Login, Einladungen und Elternbereich sind verfügbar'
                      : 'Kein Login und keine neuen Portal-Einladungen'}
                    {parentPortalSaving ? ' · Speichern…' : ''}
                    {parentPortalUpdatedAt && (
                      <>
                        <br />
                        {`Zuletzt geändert: ${new Date(parentPortalUpdatedAt).toLocaleString('de-DE')}`}
                        {parentPortalUpdatedBy ? ` von ${parentPortalUpdatedBy}` : ''}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Other General Settings */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Weitere Einstellungen</h2>
            
            {/* Zeitzone für Später senden (Admin) */}
            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6 first:pt-0 first:mt-0 first:border-t-0">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Clock className="mr-2 h-5 w-5" />
                  Zeitzone für „Später senden“
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zeitzone
                    </label>
                    <select
                      value={scheduledEmailTimezone ?? ''}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        setScheduledEmailTimezone(v);
                        if (v) saveScheduledEmailTimezone(v);
                      }}
                      disabled={scheduledEmailTimezoneLoading || scheduledEmailTimezoneSaving}
                      className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">— Nicht festgelegt (UTC) —</option>
                      <option value="Europe/Berlin">Europe/Berlin (MEZ/MESZ)</option>
                      <option value="Europe/Vienna">Europe/Vienna</option>
                      <option value="Europe/Zurich">Europe/Zurich</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="UTC">UTC</option>
                    </select>
                    {scheduledEmailTimezoneSaving && (
                      <span className="ml-2 text-sm text-gray-500">Speichern…</span>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Wird für geplante E-Mails („Später senden“) verwendet. Der Server arbeitet in UTC.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Log-Level (Admin only) */}
            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6 first:pt-0 first:mt-0 first:border-t-0">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Activity className="mr-2 h-5 w-5" />
                  System-Log-Level
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Steuert die Detailtiefe der Server-Logs (Konsole/Logdatei). Höhere Stufen zeigen weniger Meldungen. Die Änderung gilt sofort.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={logLevel}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLogLevel(v);
                      saveLogLevel(v);
                    }}
                    disabled={logLevelLoading || logLevelSaving}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {logLevelAllowedLevels.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  {(logLevelLoading || logLevelSaving) && (
                    <span className="text-sm text-gray-500">
                      {logLevelSaving ? 'Speichern...' : 'Lade...'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Zap className="mr-2 h-5 w-5" />
                  LLM-Modelle pro Verarbeitungsschritt
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Zentrale Modellsteuerung für KI-Schritte. Neben Classwork laufen auch sensible Akten-Schritte
                  über diese Profile; fuer Akten sind die auswählbaren Modelle bewusst eingeschränkt und für HUB-Routing gedacht.
                </p>
                {llmAvailability && (
                  <div
                    className={`mb-4 rounded-md border px-3 py-2 text-sm ${
                      llmAvailability.providerConfigured
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                  >
                    Provider: {llmAvailability.provider} ·{' '}
                    {llmAvailability.providerConfigured ? 'konfiguriert' : 'nicht konfiguriert (OPENAI_API_KEY fehlt)'}
                  </div>
                )}
                {!llmProfiles.length && !llmProfilesLoading ? (
                  <p className="text-sm text-gray-500">Keine LLM-Profile geladen.</p>
                ) : (
                  <div className="space-y-3">
                    {llmProfiles.map((p) => (
                      <div key={p.key} className="rounded-md border border-gray-200 bg-white p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{p.label}</div>
                            <div className="text-xs text-gray-500">{p.key}</div>
                          </div>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={p.enabled}
                              disabled={llmProfilesLoading || llmProfilesSaving}
                              onChange={(e) => {
                                const next = llmProfiles.map((x) => (x.key === p.key ? { ...x, enabled: e.target.checked } : x));
                                setLlmProfiles(next);
                                saveLlmProfiles(next);
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Aktiv
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">{p.description}</p>
                        {p.allowedModels && p.allowedModels.length > 0 && (
                          <p className="text-[11px] text-amber-700 mb-3">
                            Eingeschränkte Modellliste: {p.allowedModels.join(', ')}
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Modell</label>
                            <input
                              type="text"
                              list={`llm-model-suggestions-${p.key}`}
                              value={p.model}
                              disabled={llmProfilesLoading || llmProfilesSaving}
                              onChange={(e) => {
                                const next = llmProfiles.map((x) => (x.key === p.key ? { ...x, model: e.target.value } : x));
                                setLlmProfiles(next);
                                saveLlmProfiles(next);
                              }}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="z. B. gpt-4.1-mini"
                            />
                            <datalist id={`llm-model-suggestions-${p.key}`}>
                              {(p.allowedModels && p.allowedModels.length > 0 ? p.allowedModels : llmAllowedModels).map((m) => (
                                <option key={m} value={m}>
                                  {m}{!isModelAvailable(m) ? ' (nicht verfügbar)' : ''}
                                </option>
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Temperature</label>
                            <input
                              type="number"
                              min="0"
                              max="2"
                              step="0.1"
                              value={p.temperature}
                              disabled={llmProfilesLoading || llmProfilesSaving}
                              onChange={(e) => {
                                const next = llmProfiles.map((x) =>
                                  x.key === p.key ? { ...x, temperature: Number(e.target.value || 0) } : x
                                );
                                setLlmProfiles(next);
                              }}
                              onBlur={() => saveLlmProfiles(llmProfiles)}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Max Tokens</label>
                            <input
                              type="number"
                              min="100"
                              step="100"
                              value={p.maxTokens}
                              disabled={llmProfilesLoading || llmProfilesSaving}
                              onChange={(e) => {
                                const next = llmProfiles.map((x) =>
                                  x.key === p.key ? { ...x, maxTokens: Number(e.target.value || 1200) } : x
                                );
                                setLlmProfiles(next);
                              }}
                              onBlur={() => saveLlmProfiles(llmProfiles)}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(llmProfilesLoading || llmProfilesSaving) && (
                  <p className="mt-3 text-xs text-gray-500">{llmProfilesSaving ? 'Speichern...' : 'Lade LLM-Profile...'}</p>
                )}
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Akten-Dokumentpipeline
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Startet den clusterfähigen Backfill für bestehende Aktenbestände. Neue Uploads werden automatisch verarbeitet.
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => startStudentFileBackfillRun(false)}
                    disabled={studentFileBackfillsStarting}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Backfill starten
                  </button>
                  <button
                    type="button"
                    onClick={() => startStudentFileBackfillRun(true)}
                    disabled={studentFileBackfillsStarting}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reprocessing erzwingen
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchStudentFileBackfills()}
                    disabled={studentFileBackfillsLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Status aktualisieren
                  </button>
                </div>
                <div className="space-y-3">
                  {studentFileBackfills.map((run) => (
                    <div key={run.id} className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="text-sm font-medium text-gray-900">
                          Backfill #{run.id}
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {run.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600">
                        <div>Batchgröße: {run.batch_size}</div>
                        <div>Eingereiht: {run.queued_count}</div>
                        <div>Übersprungen: {run.skipped_count}</div>
                        <div>Fehler: {run.failed_count}</div>
                      </div>
                      {run.processing && (
                        <div className="mt-2 text-xs text-gray-500">
                          Runs: queued {run.processing.queued || 0}, running {run.processing.running || 0}, completed {run.processing.completed || 0}, failed {run.processing.failed || 0}
                        </div>
                      )}
                      {run.last_error && (
                        <div className="mt-2 text-xs text-red-600">{run.last_error}</div>
                      )}
                      <div className="mt-2 text-[11px] text-gray-400">
                        Zuletzt aktualisiert: {run.updated_at ? new Date(run.updated_at).toLocaleString('de-DE') : '-'}
                      </div>
                    </div>
                  ))}
                  {!studentFileBackfills.length && !studentFileBackfillsLoading && (
                    <p className="text-sm text-gray-500">Noch keine Akten-Backfill-Läufe vorhanden.</p>
                  )}
                  {(studentFileBackfillsLoading || studentFileBackfillsStarting) && (
                    <p className="text-xs text-gray-500">
                      {studentFileBackfillsStarting ? 'Backfill wird gestartet...' : 'Backfill-Status wird geladen...'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="mr-2 h-5 w-5" />
                  Classwork Sammel-PDF: Namensvorschlag per LLM
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Steuert die serverseitige Erkennung der <code className="mx-1">student_id</code> im Sammel-PDF-Import.
                  Bei Aktivierung wird pro Seite zuerst ein Vision-LLM genutzt; bei fehlendem Treffer folgt OCR-Fallback.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={bulkImportNameLlmSettings.enabled}
                      disabled={bulkImportNameLlmLoading || bulkImportNameLlmSaving}
                      onChange={(e) =>
                        setBulkImportNameLlmSettings((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    LLM-Vorschlag aktivieren
                  </label>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">LLM-Modell</label>
                    <input
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={bulkImportNameLlmSettings.model}
                      disabled={bulkImportNameLlmLoading || bulkImportNameLlmSaving}
                      onChange={(e) =>
                        setBulkImportNameLlmSettings((prev) => ({ ...prev, model: e.target.value }))
                      }
                      placeholder="z. B. gpt-4o-mini"
                    />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={bulkImportNameLlmLoading || bulkImportNameLlmSaving}
                    onClick={() => saveBulkImportNameLlmSettings(bulkImportNameLlmSettings)}
                  >
                    Speichern
                  </button>
                  {(bulkImportNameLlmLoading || bulkImportNameLlmSaving) ? (
                    <span className="text-xs text-gray-500">
                      {bulkImportNameLlmSaving ? 'Speichern...' : 'Lade...'}
                    </span>
                  ) : null}
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <SettingsIcon className="mr-2 h-5 w-5" />
                  Bildquellen für Unterricht
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Aktivieren Sie, welche Bildquellen im Unterricht genutzt werden dürfen.
                </p>
                <div className="space-y-3">
                  {[
                    { key: 'wikipedia', label: 'Wikipedia / Wikimedia Commons' },
                    { key: 'pixabay', label: 'Pixabay' },
                    { key: 'pexels', label: 'Pexels' },
                    { key: 'unsplash', label: 'Unsplash' },
                    { key: 'google', label: 'Google Bildersuche' }
                  ].map((source) => (
                    <label key={source.key} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                      <span className="text-sm text-gray-800">{source.label}</span>
                      <input
                        type="checkbox"
                        checked={imageSearchSourceSettings[source.key as keyof ImageSearchSourceSettings]}
                        disabled={imageSearchSourceSettingsLoading || imageSearchSourceSettingsSaving}
                        onChange={(event) => {
                          const nextSettings = {
                            ...imageSearchSourceSettings,
                            [source.key]: event.target.checked
                          };
                          setImageSearchSourceSettings(nextSettings);
                          saveImageSearchSourceSettings(nextSettings);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>
                {(imageSearchSourceSettingsLoading || imageSearchSourceSettingsSaving) && (
                  <p className="mt-3 text-xs text-gray-500">
                    {imageSearchSourceSettingsSaving ? 'Speichern...' : 'Lade Bildquellen...'}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="mr-2 h-5 w-5" />
                  ONLYOFFICE Docs
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Integration für Bearbeitung von Office-Dokumenten (z. B. in Dateien). Leer lassen, um zu deaktivieren.
                </p>
                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Die Adresse von ONLYOFFICE Docs
                    </label>
                    <input
                      type="url"
                      value={onlyofficeSettings.docsUrl}
                      onChange={(e) => setOnlyofficeSettings(s => ({ ...s, docsUrl: e.target.value }))}
                      placeholder="https://onlyoffice.lernplattform-nordeifel.de/"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={onlyofficeLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zertifikatsüberprüfung deaktivieren (unsicher)
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={onlyofficeSettings.disableSslVerify}
                        onChange={(e) => setOnlyofficeSettings(s => ({ ...s, disableSslVerify: e.target.checked }))}
                        disabled={onlyofficeLoading}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Deaktivieren</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Geheimer Schlüssel (freilassen, um zu deaktivieren)
                    </label>
                    <div className="relative">
                      <input
                        type={onlyofficeShowSecret ? 'text' : 'password'}
                        value={onlyofficeSettings.secretKey}
                        onChange={(e) => setOnlyofficeSettings(s => ({ ...s, secretKey: e.target.value }))}
                        placeholder="JWT Secret"
                        className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={onlyofficeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setOnlyofficeShowSecret(prev => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        aria-label={onlyofficeShowSecret ? 'Verbergen' : 'Anzeigen'}
                      >
                        {onlyofficeShowSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setOnlyofficeAdvancedOpen(prev => !prev)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700"
                    >
                      {onlyofficeAdvancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      Erweiterte Servereinstellungen
                    </button>
                    {onlyofficeAdvancedOpen && (
                      <div className="mt-3 space-y-3 pl-0">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Authorization-Header (leer lassen für Standard)
                          </label>
                          <input
                            type="text"
                            placeholder="Authorization"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            disabled
                            readOnly
                            title="Derzeit nicht konfigurierbar"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            ONLYOFFICE Docs – Adresse für Server-zu-Server (intern)
                          </label>
                          <input
                            type="url"
                            value={onlyofficeSettings.internalUrl}
                            onChange={(e) => setOnlyofficeSettings(s => ({ ...s, internalUrl: e.target.value }))}
                            placeholder="https://onlyoffice.lernplattform-nordeifel.de/"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={onlyofficeLoading}
                          />
                          <p className="mt-1 text-xs text-gray-500">Nur wenn nötig: Adresse des ONLYOFFICE-Servers, unter der unser Server ihn erreicht (nicht die Adresse dieser App).</p>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Adresse dieser App für ONLYOFFICE (Callback-Basis-URL)
                          </label>
                          <input
                            type="url"
                            value={onlyofficeSettings.callbackBaseUrl}
                            onChange={(e) => setOnlyofficeSettings(s => ({ ...s, callbackBaseUrl: e.target.value }))}
                            placeholder="https://apollo.lernplattform-nordeifel.de/"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={onlyofficeLoading}
                          />
                          <p className="mt-1 text-xs text-gray-500">URL dieser Lernrakete-Instanz, wie sie vom ONLYOFFICE-Server aus erreichbar ist (für Dokument-Abruf und Callback).</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={saveOnlyofficeSettings}
                      disabled={onlyofficeLoading || onlyofficeSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      Speichern
                    </button>
                    {(onlyofficeLoading || onlyofficeSaving) && (
                      <span className="text-sm text-gray-500">
                        {onlyofficeSaving ? 'Speichern...' : 'Lade...'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <FolderOpen className="mr-2 h-5 w-5" />
                  Dateispeicher aktivieren
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Legen Sie fest, welche Speicherorte in Lernrakete verfügbar sind. Die Optionen sind unabhängig voneinander schaltbar.
                </p>
                <div className="space-y-3 max-w-xl mb-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={storageProviders.lernraketeEnabled}
                      onChange={(e) => updateStorageProvider({ lernraketeEnabled: e.target.checked })}
                      disabled={storageProvidersLoading || storageProvidersSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    Interne Lernrakete-Dateiablage („Dateien“)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={storageProviders.nextcloudEnabled}
                      onChange={(e) => updateStorageProvider({ nextcloudEnabled: e.target.checked })}
                      disabled={storageProvidersLoading || storageProvidersSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    Nextcloud
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={storageProviders.onedriveEnabled}
                      onChange={(e) => updateStorageProvider({ onedriveEnabled: e.target.checked })}
                      disabled={storageProvidersLoading || storageProvidersSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    Microsoft OneDrive
                  </label>
                  <p className="text-xs text-gray-500">
                    Änderungen werden beim An- oder Abwählen automatisch gespeichert (nicht über den OnlyOffice-Speichern-Button darüber).
                  </p>
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => void saveStorageProvidersSettings()}
                      disabled={storageProvidersLoading || storageProvidersSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      Speichern
                    </button>
                    {(storageProvidersLoading || storageProvidersSaving) && (
                      <span className="text-sm text-gray-500">{storageProvidersSaving ? 'Speichern...' : 'Lade...'}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && storageProviders.nextcloudEnabled && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Cloud className="mr-2 h-5 w-5" />
                  Nextcloud
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Schul-Nextcloud für „Dateien → Meine Dateien“. Nutzer hinterlegen Benutzername und App-Passwort in den persönlichen Einstellungen.
                </p>
                <div className="space-y-4 max-w-xl">
                  <fieldset className="space-y-2">
                    <legend className="block text-sm font-medium text-gray-700 mb-2">Erlaubte Nextcloud-Konten</legend>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="nextcloudAccountMode"
                        checked={nextcloudSettings.accountMode === 'school_and_personal'}
                        onChange={() => setNextcloudSettings(s => ({ ...s, accountMode: 'school_and_personal' }))}
                        disabled={nextcloudLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Schul- und private Nextcloud</span>
                        <span className="block text-xs text-gray-500">Schul-Server plus eigene private Nextcloud-Instanz (eigene URL pro Nutzer)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="nextcloudAccountMode"
                        checked={nextcloudSettings.accountMode === 'school_only'}
                        onChange={() => setNextcloudSettings(s => ({ ...s, accountMode: 'school_only' }))}
                        disabled={nextcloudLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Nur Schul-Nextcloud</span>
                        <span className="block text-xs text-gray-500">Alle Nutzer verbinden sich nur mit dem Schul-Server</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="nextcloudAccountMode"
                        checked={nextcloudSettings.accountMode === 'custom_tenant'}
                        onChange={() => setNextcloudSettings(s => ({ ...s, accountMode: 'custom_tenant' }))}
                        disabled={nextcloudLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Nur ein bestimmter Nextcloud-Server</span>
                        <span className="block text-xs text-gray-500">Feste Server-URL (z. B. nur ein externer Dienstleister)</span>
                      </span>
                    </label>
                  </fieldset>
                  {nextcloudSettings.accountMode === 'custom_tenant' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nextcloud-Server-URL (fest)
                      </label>
                      <input
                        type="url"
                        value={nextcloudSettings.customServerUrl}
                        onChange={(e) => setNextcloudSettings(s => ({ ...s, customServerUrl: e.target.value }))}
                        placeholder="https://cloud.example.de"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={nextcloudLoading}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Schul-Nextcloud-Server-URL
                      </label>
                      <input
                        type="url"
                        value={nextcloudSettings.baseUrl}
                        onChange={(e) => setNextcloudSettings(s => ({ ...s, baseUrl: e.target.value }))}
                        placeholder="https://cloud.schule.de"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={nextcloudLoading}
                      />
                    </div>
                  )}
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={nextcloudSettings.useLernraketeCredentials}
                        onChange={(e) => setNextcloudSettings(s => ({ ...s, useLernraketeCredentials: e.target.checked }))}
                        disabled={nextcloudLoading}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Zugangsdaten wie Lernrakete (gleicher Benutzername und Passwort wie beim Anmelden)</span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-gray-500">
                      Nutzer müssen kein separates App-Passwort hinterlegen. Beim Login wird die Verbindung automatisch geprüft (bei 2FA: einmalig Passwort in den Einstellungen bestätigen).
                    </p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={nextcloudSettings.disableSslVerify}
                        onChange={(e) => setNextcloudSettings(s => ({ ...s, disableSslVerify: e.target.checked }))}
                        disabled={nextcloudLoading}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Zertifikatsüberprüfung deaktivieren (unsicher)</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={saveNextcloudSettings}
                      disabled={nextcloudLoading || nextcloudSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      Speichern
                    </button>
                    {(nextcloudLoading || nextcloudSaving) && (
                      <span className="text-sm text-gray-500">
                        {nextcloudSaving ? 'Speichern...' : 'Lade...'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'system-admin' && isAdmin && storageProviders.onedriveEnabled && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Cloud className="mr-2 h-5 w-5 text-sky-600" />
                  Microsoft OneDrive
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Azure App Registration (Client ID, Secret). Redirect-URI in Azure:{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">…/api/onedrive/auth/callback</code>
                  {' '}(oder hier explizit eintragen). Nutzer verbinden per Microsoft-Anmeldung mit dem eigenen Konto.
                </p>
                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application (client) ID</label>
                    <input
                      type="text"
                      value={onedriveSettings.clientId}
                      onChange={(e) => setOnedriveSettings(s => ({ ...s, clientId: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                      disabled={onedriveLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client secret</label>
                    <div className="relative">
                      <input
                        type={onedriveShowSecret ? 'text' : 'password'}
                        value={onedriveSettings.clientSecret}
                        onChange={(e) => setOnedriveSettings(s => ({ ...s, clientSecret: e.target.value }))}
                        placeholder="Neu eingeben zum Ändern"
                        className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm"
                        disabled={onedriveLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setOnedriveShowSecret(p => !p)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500"
                      >
                        {onedriveShowSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <fieldset className="space-y-2">
                    <legend className="block text-sm font-medium text-gray-700 mb-2">Erlaubte Microsoft-Konten</legend>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="onedriveAccountMode"
                        checked={onedriveSettings.accountMode === 'school_and_personal'}
                        onChange={() => setOnedriveSettings(s => ({ ...s, accountMode: 'school_and_personal' }))}
                        disabled={onedriveLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Schul- und private Konten</span>
                        <span className="block text-xs text-gray-500">Schul-M365 und persönliche Microsoft-Konten (z. B. @outlook.com)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="onedriveAccountMode"
                        checked={onedriveSettings.accountMode === 'school_only'}
                        onChange={() => setOnedriveSettings(s => ({ ...s, accountMode: 'school_only' }))}
                        disabled={onedriveLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Nur Schul-/Arbeitskonten</span>
                        <span className="block text-xs text-gray-500">Nur Microsoft-365-Konten von Schulen und Organisationen, keine privaten Konten</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="onedriveAccountMode"
                        checked={onedriveSettings.accountMode === 'custom_tenant'}
                        onChange={() => setOnedriveSettings(s => ({ ...s, accountMode: 'custom_tenant' }))}
                        disabled={onedriveLoading}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Nur eine bestimmte Tenant-ID</span>
                        <span className="block text-xs text-gray-500">Nur Konten Ihrer Azure-Organisation (GUID aus dem Azure-Portal)</span>
                      </span>
                    </label>
                    {onedriveSettings.accountMode === 'custom_tenant' && (
                      <div className="ml-6 mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tenant-ID (GUID)</label>
                        <input
                          type="text"
                          value={onedriveSettings.customTenantId}
                          onChange={(e) => setOnedriveSettings(s => ({ ...s, customTenantId: e.target.value }))}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm font-mono text-sm"
                          disabled={onedriveLoading}
                        />
                      </div>
                    )}
                  </fieldset>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Redirect-URI (optional)</label>
                    <input
                      type="url"
                      value={onedriveSettings.redirectUri}
                      onChange={(e) => setOnedriveSettings(s => ({ ...s, redirectUri: e.target.value }))}
                      placeholder="https://ihre-lernrakete.de/api/onedrive/auth/callback"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                      disabled={onedriveLoading}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={saveOnedriveSettings}
                      disabled={onedriveLoading || onedriveSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      Speichern
                    </button>
                    {(onedriveLoading || onedriveSaving) && (
                      <span className="text-sm text-gray-500">{onedriveSaving ? 'Speichern...' : 'Lade...'}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Schulweit: inaktive Schüler (Klasse attic) in Auswahllisten */}
            {activeTab === 'organization' && canManageSchoolSettings && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Users className="mr-2 h-5 w-5" />
                  Schüler-Auswahllisten
                </h3>
                <div className="flex items-start gap-3 max-w-2xl">
                  <input
                    type="checkbox"
                    id="hide-inactive-students-attic"
                    checked={hideInactiveStudentsFromAttic}
                    disabled={hideInactiveStudentsLoading}
                    onChange={async e => {
                      const next = e.target.checked;
                      setHideInactiveStudentsLoading(true);
                      try {
                        await api.put(
                          `/settings/${encodeURIComponent('students.hide_inactive_attic')}`,
                          { value: next ? 'true' : 'false' }
                        );
                        setHideInactiveStudentsFromAttic(next);
                        toast.success('Einstellung gespeichert');
                      } catch (err: unknown) {
                        console.error(err);
                        const msg =
                          err && typeof err === 'object' && 'response' in err
                            ? (err as { response?: { data?: { message?: string } } }).response?.data
                                ?.message
                            : undefined;
                        toast.error(msg || 'Fehler beim Speichern');
                      } finally {
                        setHideInactiveStudentsLoading(false);
                      }
                    }}
                    className="h-4 w-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <label
                      htmlFor="hide-inactive-students-attic"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      Inaktive Schüler ausblenden
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      Wenn aktiv, werden Schüler der Klasse „attic“ oder „Attic“ in allen Schülerauswahllisten nicht
                      angezeigt (Sophomorix-Inaktiv-Klasse).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Student Email Policy */}
            {activeTab === 'organization' && canManageSchoolSettings && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <Mail className="mr-2 h-5 w-5" />
                Schüler-E-Mail Einstellungen
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mindestalter für Schüler-E-Mail (Jahre)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="25"
                    value={studentEmailMinAge}
                    onChange={(e) => setStudentEmailMinAge(parseInt(e.target.value, 10) || 16)}
                    onBlur={async () => {
                      try {
                        setStudentEmailPolicyLoading(true);
                        await api.post('/settings/student-email-policy', {
                          minAgeYears: studentEmailMinAge
                        });
                        toast.success('Einstellung gespeichert');
                      } catch (error: any) {
                        console.error('Error saving student email policy:', error);
                        toast.error('Fehler beim Speichern der Einstellung');
                      } finally {
                        setStudentEmailPolicyLoading(false);
                      }
                    }}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Schüler ab diesem Alter können als E-Mail-Empfänger verwendet werden (z.B. in Sek II)
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* Official file and queue permissions */}
            {activeTab === 'organization' && isAdmin && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Schülerakte – Zugriff auf offizielle Akten
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Bestimmen Sie, welche Rollen oder Benutzer Zugriff auf offizielle Akten haben. Betroffene können: offizielle Akten einsehen, Dateien zur SL-Queue einreichen sowie (bei Klassenlehrer für ihre Klasse) genehmigen. Admin, Schulleitung und Abteilungsleiter haben immer vollen Zugriff.
              </p>
              
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-2 text-xs text-gray-500">
                  Debug: Rollen: {queueAvailableRoles.length}, Benutzer: {queueAvailableUsers.length}, Loading: {queuePermissionsLoading ? 'Ja' : 'Nein'}
                </div>
              )}
              
              {queuePermissionsLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Lade Berechtigungen...</p>
                </div>
              ) : (
                <>
                  {/* Add new permission */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <select
                        value={newPermissionType}
                        onChange={(e) => {
                          setNewPermissionType(e.target.value as 'role' | 'user');
                          setNewPermissionValue('');
                          setUserSearchTerm('');
                          setShowUserSearchResults(false);
                        }}
                        className="input input-sm border-gray-300 w-full sm:w-auto sm:min-w-[120px]"
                      >
                        <option value="role">Rolle</option>
                        <option value="user">Benutzer</option>
                      </select>
                      {newPermissionType === 'role' ? (
                        <select
                          value={newPermissionValue}
                          onChange={(e) => {
                            console.log('Role selected:', e.target.value);
                            setNewPermissionValue(e.target.value);
                          }}
                          className="flex-1 input input-sm border-gray-300 min-w-0"
                          disabled={queuePermissionsLoading}
                        >
                          <option value="">
                            {queuePermissionsLoading 
                              ? 'Lade Rollen...' 
                              : queueAvailableRoles.length === 0 
                                ? 'Keine Rollen verfügbar' 
                                : 'Rolle auswählen...'}
                          </option>
                          {queueAvailableRoles.map(role => (
                            <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex-1 relative min-w-0" ref={userSearchRef}>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="text"
                              value={userSearchTerm}
                              onChange={(e) => {
                                setUserSearchTerm(e.target.value);
                                setShowUserSearchResults(true);
                                if (!e.target.value.trim()) {
                                  setNewPermissionValue('');
                                }
                              }}
                              onFocus={() => setShowUserSearchResults(true)}
                              placeholder={queuePermissionsLoading 
                                ? 'Lade Benutzer...' 
                                : queueAvailableUsers.length === 0 
                                  ? 'Keine Benutzer verfügbar' 
                                  : 'Benutzer suchen...'}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              disabled={queuePermissionsLoading}
                            />
                            {newPermissionValue && (
                              <button
                                onClick={() => {
                                  setNewPermissionValue('');
                                  setUserSearchTerm('');
                                  setShowUserSearchResults(false);
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                type="button"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          {/* Search Results Dropdown */}
                          {showUserSearchResults && filteredUsersForSearch.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {filteredUsersForSearch.map((user: any) => {
                                const isSelected = newPermissionValue === user.username;
                                return (
                                  <div
                                    key={user.id}
                                    onClick={() => {
                                      setNewPermissionValue(user.username);
                                      setUserSearchTerm(`${user.first_name || ''} ${user.last_name || ''} (${user.username})`);
                                      setShowUserSearchResults(false);
                                    }}
                                    className={`px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                                      isSelected ? 'bg-blue-100' : ''
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium text-sm">
                                          {user.first_name || ''} {user.last_name || ''}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {user.username} - {getRoleDisplayName(user.role)}
                                        </div>
                                      </div>
                                      {isSelected && (
                                        <CheckCircle className="w-4 h-4 text-blue-600" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {filteredUsersForSearch.length === 10 && userSearchTerm && (
                                <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200">
                                  Weitere Ergebnisse verfügbar. Bitte verfeinern Sie Ihre Suche.
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Show selected user */}
                          {newPermissionValue && !showUserSearchResults && (
                            <div className="mt-1 text-xs text-gray-600">
                              Ausgewählt: {queueAvailableUsers.find((u: any) => u.username === newPermissionValue)?.first_name || ''} {queueAvailableUsers.find((u: any) => u.username === newPermissionValue)?.last_name || ''}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={addQueuePermission}
                        disabled={!newPermissionValue || queuePermissionsLoading}
                        className="btn btn-primary btn-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        title={!newPermissionValue ? 'Bitte wählen Sie eine Rolle oder einen Benutzer aus' : ''}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Hinzufügen
                      </button>
                    </div>
                  </div>

                  {/* Permissions list */}
                  <div className="space-y-2">
                    {queuePermissions.filter(p => p.is_active).length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        Keine Berechtigungen konfiguriert. Nur Admin, Schulleitung und Abteilungsleiter haben Zugriff auf offizielle Akten.
                      </div>
                    ) : (
                      queuePermissions
                        .filter(p => p.is_active)
                        .map((permission) => (
                          <div
                            key={permission.permission_id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center space-x-3">
                              {permission.permission_type === 'role' ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(permission.permission_value)}`}>
                                  {getRoleIcon(permission.permission_value)} {getRoleDisplayName(permission.permission_value)}
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  👤 {permission.permission_value}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                ({permission.permission_type === 'role' ? 'Rolle' : 'Benutzer'})
                              </span>
                            </div>
                            <button
                              onClick={() => removeQueuePermission(permission.permission_id)}
                              className="btn btn-danger btn-sm flex items-center"
                              title="Entfernen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))
                    )}
                  </div>
                </>
              )}
            </div>
            )}

            {/* Email Labels Management */}
            {activeTab === 'organization' && canManageSchoolSettings && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <Tag className="mr-2 h-5 w-5" />
                E-Mail Labels verwalten
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Erstellen und verwalten Sie Labels für E-Mails. Definieren Sie Kriterien für die automatische LLM-Klassifikation.
              </p>

              {/* Create/Edit Label Form */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  {editingLabel ? 'Label bearbeiten' : 'Neues Label erstellen'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={editingLabel?.name || newLabel.name}
                      onChange={(e) => {
                        if (editingLabel) {
                          setEditingLabel({ ...editingLabel, name: e.target.value });
                        } else {
                          setNewLabel({ ...newLabel, name: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="z.B. Elterngespräch"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Farbe</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={editingLabel?.color || newLabel.color}
                        onChange={(e) => {
                          if (editingLabel) {
                            setEditingLabel({ ...editingLabel, color: e.target.value });
                          } else {
                            setNewLabel({ ...newLabel, color: e.target.value });
                          }
                        }}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editingLabel?.color || newLabel.color}
                        onChange={(e) => {
                          if (editingLabel) {
                            setEditingLabel({ ...editingLabel, color: e.target.value });
                          } else {
                            setNewLabel({ ...newLabel, color: e.target.value });
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                        placeholder="#6B7280"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Beschreibung</label>
                    <input
                      type="text"
                      value={editingLabel?.description || newLabel.description}
                      onChange={(e) => {
                        if (editingLabel) {
                          setEditingLabel({ ...editingLabel, description: e.target.value });
                        } else {
                          setNewLabel({ ...newLabel, description: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Kurze Beschreibung des Labels"
                    />
                  </div>
                </div>

                {/* LLM Criteria */}
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h5 className="text-xs font-medium text-gray-900 mb-3">LLM-Klassifikationskriterien</h5>
                  
                  {/* Keywords */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Schlüsselwörter</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const keyword = newKeyword.trim();
                            if (keyword) {
                              const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                              const keywords = [...(criteria.keywords || []), keyword];
                              if (editingLabel) {
                                setEditingLabel({
                                  ...editingLabel,
                                  llm_criteria: { ...criteria, keywords }
                                });
                              } else {
                                setNewLabel({
                                  ...newLabel,
                                  llm_criteria: { ...criteria, keywords }
                                });
                              }
                              setNewKeyword('');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Schlüsselwort eingeben und Enter drücken"
                      />
                      <button
                        onClick={() => {
                          const keyword = newKeyword.trim();
                          if (keyword) {
                            const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                            const keywords = [...(criteria.keywords || []), keyword];
                            if (editingLabel) {
                              setEditingLabel({
                                ...editingLabel,
                                llm_criteria: { ...criteria, keywords }
                              });
                            } else {
                              setNewLabel({
                                ...newLabel,
                                llm_criteria: { ...criteria, keywords }
                              });
                            }
                            setNewKeyword('');
                          }
                        }}
                        className="btn btn-primary btn-sm"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(editingLabel?.llm_criteria?.keywords || newLabel.llm_criteria.keywords || []).map((kw: string, idx: number) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          {kw}
                          <button
                            onClick={() => {
                              const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                              const keywords = (criteria.keywords || []).filter((_: string, i: number) => i !== idx);
                              if (editingLabel) {
                                setEditingLabel({
                                  ...editingLabel,
                                  llm_criteria: { ...criteria, keywords }
                                });
                              } else {
                                setNewLabel({
                                  ...newLabel,
                                  llm_criteria: { ...criteria, keywords }
                                });
                              }
                            }}
                            className="hover:text-blue-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Patterns */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Muster (Regex)</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newPattern}
                        onChange={(e) => setNewPattern(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const pattern = newPattern.trim();
                            if (pattern) {
                              const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                              const patterns = [...(criteria.patterns || []), pattern];
                              if (editingLabel) {
                                setEditingLabel({
                                  ...editingLabel,
                                  llm_criteria: { ...criteria, patterns }
                                });
                              } else {
                                setNewLabel({
                                  ...newLabel,
                                  llm_criteria: { ...criteria, patterns }
                                });
                              }
                              setNewPattern('');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                        placeholder="z.B. ^Fehlzeit|^Entschuldigung"
                      />
                      <button
                        onClick={() => {
                          const pattern = newPattern.trim();
                          if (pattern) {
                            const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                            const patterns = [...(criteria.patterns || []), pattern];
                            if (editingLabel) {
                              setEditingLabel({
                                ...editingLabel,
                                llm_criteria: { ...criteria, patterns }
                              });
                            } else {
                              setNewLabel({
                                ...newLabel,
                                llm_criteria: { ...criteria, patterns }
                              });
                            }
                            setNewPattern('');
                          }
                        }}
                        className="btn btn-primary btn-sm"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(editingLabel?.llm_criteria?.patterns || newLabel.llm_criteria.patterns || []).map((p: string, idx: number) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-mono"
                        >
                          {p}
                          <button
                            onClick={() => {
                              const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                              const patterns = (criteria.patterns || []).filter((_: string, i: number) => i !== idx);
                              if (editingLabel) {
                                setEditingLabel({
                                  ...editingLabel,
                                  llm_criteria: { ...criteria, patterns }
                                });
                              } else {
                                setNewLabel({
                                  ...newLabel,
                                  llm_criteria: { ...criteria, patterns }
                                });
                              }
                            }}
                            className="hover:text-purple-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Priorität</label>
                    <select
                      value={editingLabel?.llm_criteria?.priority || newLabel.llm_criteria.priority}
                      onChange={(e) => {
                        const priority = e.target.value as 'low' | 'medium' | 'high';
                        const criteria = editingLabel?.llm_criteria || newLabel.llm_criteria;
                        if (editingLabel) {
                          setEditingLabel({
                            ...editingLabel,
                            llm_criteria: { ...criteria, priority }
                          });
                        } else {
                          setNewLabel({
                            ...newLabel,
                            llm_criteria: { ...criteria, priority }
                          });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="low">Niedrig</option>
                      <option value="medium">Mittel</option>
                      <option value="high">Hoch</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      if (editingLabel) {
                        saveEmailLabel(editingLabel);
                      } else {
                        saveEmailLabel(newLabel);
                      }
                    }}
                    disabled={!(editingLabel?.name || newLabel.name)}
                    className="btn btn-primary btn-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    {editingLabel ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                  {editingLabel && (
                    <button
                      onClick={() => {
                        setEditingLabel(null);
                        setNewLabel({
                          name: '',
                          description: '',
                          color: '#6B7280',
                          icon: 'tag',
                          llm_criteria: {
                            keywords: [],
                            patterns: [],
                            context_rules: [],
                            priority: 'medium'
                          }
                        });
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      Abbrechen
                    </button>
                  )}
                </div>
              </div>

              {/* Labels List */}
              <div className="space-y-2">
                {emailLabelsLoading ? (
                  <div className="text-center py-4 text-gray-500 text-sm">Lade Labels...</div>
                ) : emailLabels.filter((l: any) => !l.is_system).length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Keine Labels erstellt. Erstellen Sie Ihr erstes Label oben.
                  </div>
                ) : (
                  emailLabels
                    .filter((l: any) => !l.is_system)
                    .map((label: any) => (
                      <div
                        key={label.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: label.color || '#6B7280' }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{label.name}</span>
                              {label.description && (
                                <span className="text-xs text-gray-500">- {label.description}</span>
                              )}
                            </div>
                            {label.llm_criteria && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {label.llm_criteria.keywords && label.llm_criteria.keywords.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {label.llm_criteria.keywords.length} Schlüsselwörter
                                  </span>
                                )}
                                {label.llm_criteria.patterns && label.llm_criteria.patterns.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {label.llm_criteria.patterns.length} Muster
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingLabel({
                                ...label,
                                llm_criteria: label.llm_criteria || {
                                  keywords: [],
                                  patterns: [],
                                  context_rules: [],
                                  priority: 'medium'
                                }
                              });
                              setNewLabel({
                                name: '',
                                description: '',
                                color: '#6B7280',
                                icon: 'tag',
                                llm_criteria: {
                                  keywords: [],
                                  patterns: [],
                                  context_rules: [],
                                  priority: 'medium'
                                }
                              });
                            }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Bearbeiten"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteEmailLabel(label.id)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Löschen"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Darstellung (Persönlich & Konto) */}
      {activeTab === 'appearance' && (
        <div className="bg-white shadow rounded-lg p-6 dark:bg-gray-900 dark:border dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 flex items-center gap-2">
            <SunMoon className="h-5 w-5 shrink-0" aria-hidden />
            Darstellung
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Farbschema und Schriftgröße gelten nur auf diesem Gerät und bleiben für Ihren nächsten Besuch gespeichert.
          </p>
          <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:flex-wrap">
            <div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Farbschema</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(
                  [
                    { value: 'light' as const, label: 'Aus' },
                    { value: 'system' as const, label: 'System' },
                    { value: 'dark' as const, label: 'An' }
                  ] satisfies { value: LrThemePreference; label: string }[]
                ).map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={uiThemePreference === opt.value ? 'primary' : 'outline'}
                    onClick={() => {
                      setThemePreference(opt.value);
                      setUiThemePreference(opt.value);
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Schriftgröße</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(
                  [
                    { value: 'xs' as const, label: 'Sehr klein' },
                    { value: 'sm' as const, label: 'Klein (S)' },
                    { value: 'md' as const, label: 'Mittel (M)' },
                    { value: 'lg' as const, label: 'Groß (L)' },
                    { value: 'xl' as const, label: 'Sehr groß' }
                  ] satisfies { value: LrTextScale; label: string }[]
                ).map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={uiTextScale === opt.value ? 'primary' : 'outline'}
                    onClick={() => {
                      setTextScale(opt.value);
                      setUiTextScale(opt.value);
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Personal Data Tab */}
      {activeTab === 'personal' && (
        <div className="space-y-6">
          {personalDataLoading ? (
            <div className="bg-white shadow rounded-lg p-6">
              <p className="text-center text-gray-500">Lädt persönliche Daten...</p>
            </div>
          ) : personalData ? (
            <>
              {/* Personal Information */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center">
                    <User className="mr-2 h-5 w-5" />
                    Persönliche Informationen
                  </h2>
                  
                  {/* Save Status Indicator */}
                  <div className="flex items-center space-x-2">
                    {saveStatus === 'saving' && (
                      <span className="text-sm text-blue-600 flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Speichert...
                      </span>
                    )}
                    {saveStatus === 'saved' && (
                      <span className="text-sm text-green-600 flex items-center">
                        <UserCheck className="mr-1 h-4 w-4" />
                        Gespeichert
                      </span>
                    )}
                    {saveStatus === 'error' && (
                      <span className="text-sm text-red-600 flex items-center">
                        <UserX className="mr-1 h-4 w-4" />
                        Fehler
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vorname
                    </label>
                    <input
                      type="text"
                      value={personalData.firstName}
                      onChange={(e) => setPersonalData(prev => prev ? {...prev, firstName: e.target.value} : null)}
                      onBlur={() => updatePersonalData({firstName: personalData.firstName})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ihr Vorname"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nachname
                    </label>
                    <input
                      type="text"
                      value={personalData.lastName}
                      onChange={(e) => setPersonalData(prev => prev ? {...prev, lastName: e.target.value} : null)}
                      onBlur={() => updatePersonalData({lastName: personalData.lastName})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ihr Nachname"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Angezeigter Name
                    </label>
                    <input
                      type="text"
                      value={personalData.displayName}
                      onChange={(e) => setPersonalData(prev => prev ? {...prev, displayName: e.target.value} : null)}
                      onBlur={() => updatePersonalData({displayName: personalData.displayName})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="z.B. Herr Müller, Frau Schmidt"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Wie Sie in Nachrichten und für andere Benutzer angezeigt werden
                    </p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Briefcase className="inline mr-1 h-4 w-4" />
                      Privates PayPal Konto
                    </label>
                    <input
                      type="email"
                      value={personalData.paypalAccount}
                      onChange={(e) => setPersonalData(prev => prev ? {...prev, paypalAccount: e.target.value} : null)}
                      onBlur={() => updatePersonalData({paypalAccount: personalData.paypalAccount})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="paypal@example.com"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Für Zahlungen und Erstattungen (optional)
                    </p>
                  </div>
                </div>
              </div>

              {/* LLM Communication Settings */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <Zap className="mr-2 h-5 w-5" />
                  Nachrichten-Automatik Einstellungen
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Steuern Sie den Ton und Stil von automatisch generierten Nachrichten an Eltern und Schüler
                </p>
                
                <div className="space-y-8">
                  {/* Herzlichkeit Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <Heart className="mr-2 h-4 w-4 text-red-500" />
                        Herzlichkeit
                      </label>
                      <span className="text-sm text-gray-500">{personalData.llmSettings.herzlichkeit}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={personalData.llmSettings.herzlichkeit}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        setPersonalData(prev => prev ? {
                          ...prev,
                          llmSettings: {...prev.llmSettings, herzlichkeit: newValue}
                        } : null);
                      }}
                      onMouseUp={() => updatePersonalData({llmSettings: personalData.llmSettings})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #fee2e2 0%, #fca5a5 ${personalData.llmSettings.herzlichkeit}%, #e5e7eb ${personalData.llmSettings.herzlichkeit}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Kühl, sachlich</span>
                      <span>Warm, freundlich, zugewandt</span>
                    </div>
                  </div>

                  {/* Förmlichkeit Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <Shield className="mr-2 h-4 w-4 text-blue-500" />
                        Förmlichkeit
                      </label>
                      <span className="text-sm text-gray-500">{personalData.llmSettings.foermlichkeit}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={personalData.llmSettings.foermlichkeit}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        setPersonalData(prev => prev ? {
                          ...prev,
                          llmSettings: {...prev.llmSettings, foermlichkeit: newValue}
                        } : null);
                      }}
                      onMouseUp={() => updatePersonalData({llmSettings: personalData.llmSettings})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #dbeafe 0%, #93c5fd ${personalData.llmSettings.foermlichkeit}%, #e5e7eb ${personalData.llmSettings.foermlichkeit}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Locker, informell</span>
                      <span>Höflich, formell</span>
                    </div>
                  </div>

                  {/* Bestimmtheit Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <Zap className="mr-2 h-4 w-4 text-yellow-500" />
                        Bestimmtheit
                      </label>
                      <span className="text-sm text-gray-500">{personalData.llmSettings.bestimmtheit}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={personalData.llmSettings.bestimmtheit}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        setPersonalData(prev => prev ? {
                          ...prev,
                          llmSettings: {...prev.llmSettings, bestimmtheit: newValue}
                        } : null);
                      }}
                      onMouseUp={() => updatePersonalData({llmSettings: personalData.llmSettings})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #fef3c7 0%, #fbbf24 ${personalData.llmSettings.bestimmtheit}%, #e5e7eb ${personalData.llmSettings.bestimmtheit}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Offen, wahlfrei</span>
                      <span>Klar, verbindlich</span>
                    </div>
                  </div>
                </div>

                {/* Preview Section */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Vorschau der Nachrichten-Automatik</h3>
                  <div className="text-sm text-gray-600">
                    <p><strong>Herzlichkeit:</strong> {
                      personalData.llmSettings.herzlichkeit < 30 ? 'Sachlich und professionell' :
                      personalData.llmSettings.herzlichkeit < 70 ? 'Freundlich und ausgewogen' :
                      'Sehr warm und zugewandt'
                    }</p>
                    <p><strong>Förmlichkeit:</strong> {
                      personalData.llmSettings.foermlichkeit < 30 ? 'Locker und informell' :
                      personalData.llmSettings.foermlichkeit < 70 ? 'Höflich und angemessen' :
                      'Sehr formell und respektvoll'
                    }</p>
                    <p><strong>Bestimmtheit:</strong> {
                      personalData.llmSettings.bestimmtheit < 30 ? 'Offen für Diskussion' :
                      personalData.llmSettings.bestimmtheit < 70 ? 'Klar mit Spielraum' :
                      'Sehr bestimmt und verbindlich'
                    }</p>
                  </div>
                </div>
              </div>

              {/* Email Credentials Section */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center">
                    <Mail className="mr-2 h-5 w-5" />
                    E-Mail Konfiguration
                  </h2>
                  <button
                    onClick={() => {
                      setEditingEmailCredential(null);
                      setShowEmailCredentialModal(true);
                    }}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    E-Mail Konto hinzufügen
                  </button>
                </div>

                {userEmailLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Lade E-Mail-Konten...</p>
                  </div>
                ) : userEmailCredentials.length > 0 ? (
                  <div className="space-y-4">
                    {userEmailCredentials.map((credential) => (
                      <div key={credential.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <Mail className="w-5 h-5 text-gray-400 mr-3" />
                              <div>
                                <h3 className="font-medium text-gray-900">
                                  {credential.email_address}
                                  {credential.is_default && (
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                      Standard
                                    </span>
                                  )}
                                </h3>
                                <p className="text-sm text-gray-500">
                                  {credential.display_name || 'Kein Anzeigename'}
                                </p>
                                {credential.test_status && (
                                  <div className={`flex items-center mt-1 text-xs ${
                                    credential.test_status === 'success' 
                                      ? 'text-green-600' 
                                      : credential.test_status === 'failed' 
                                      ? 'text-red-600' 
                                      : 'text-gray-500'
                                  }`}>
                                    {credential.test_status === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                                    {credential.test_status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                                    {credential.test_status === 'success' && 'Verbindung erfolgreich'}
                                    {credential.test_status === 'failed' && 'Verbindung fehlgeschlagen'}
                                    {credential.test_status === 'untested' && 'Nicht getestet'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => testEmailConnection(credential.email_address)}
                              disabled={testingEmails.has(credential.email_address)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                              title="Verbindung testen"
                            >
                              {testingEmails.has(credential.email_address) ? (
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <TestTube className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setEditingEmailCredential(credential);
                                setShowEmailCredentialModal(true);
                              }}
                              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors"
                              title="Bearbeiten"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm('Möchten Sie dieses E-Mail-Konto wirklich löschen?')) {
                                  deleteUserEmailCredentials(credential.id!);
                                }
                              }}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              title="Löschen"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Keine E-Mail-Konten konfiguriert</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Fügen Sie ein E-Mail-Konto hinzu, um E-Mails über Lernrakete zu senden.
                    </p>
                    <button
                      onClick={() => {
                        setEditingEmailCredential(null);
                        setShowEmailCredentialModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Erstes E-Mail-Konto hinzufügen
                    </button>
                  </div>
                )}
              </div>

              {storagePublic?.nextcloud?.enabled !== false && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                    <Cloud className="mr-2 h-5 w-5" />
                    Nextcloud
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Verbinden Sie Ihr Nextcloud-Konto, um Ihre Dateien unter „Dateien → Meine Dateien“ zu sehen.
                  </p>
                  <NextcloudCredentialsPanel adminConfigured={nextcloudAdminConfigured} />
                </div>
              )}

              {storagePublic?.onedrive?.enabled !== false && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                    <Cloud className="mr-2 h-5 w-5 text-sky-600" />
                    OneDrive
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Verbinden Sie Ihr Microsoft-OneDrive, um Dateien unter „Dateien → Meine Dateien“ zu nutzen.
                  </p>
                  <OneDriveCredentialsPanel adminConfigured={onedriveAdminConfigured} />
                </div>
              )}
            </>
          ) : (
            <div className="bg-white shadow rounded-lg p-6">
              <p className="text-center text-gray-500">Fehler beim Laden der persönlichen Daten</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'security' && (
        <SecurityPanel isAdmin={isAdmin} />
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && isAdmin && (
        <div className="space-y-6">
          <MigrationStatusPanel />

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Shield className="h-6 w-6 mr-2 text-orange-600" />
                  Wartungsfunktionen
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Administratoren-Werkzeuge für Systemwartung
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Foto-Optimierung */}
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Camera className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Foto-Optimierung
                    </h3>
                    <p className="text-sm text-gray-500">
                      Schülerfotos optimieren
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Verwende Gesichtserkennung und Auto-Crop, um Schülerfotos zu optimieren. 
                    Helligkeit und Kontrast werden automatisch angepasst.
                  </p>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    Client-seitige Bildverarbeitung (DSGVO-konform)
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    Automatische Gesichtserkennung
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    Auto-Crop für Bildschirmfüllung
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <a
                    href="/foto-optimierung"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Camera className="h-4 w-4" />
                    Foto-Optimierung öffnen
                  </a>
                </div>
              </div>

              {/* DSGVO User-Daten-Verwaltung */}
              <UserDataManagement />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'navigation-permissions' && isAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <NavigationPermissionsPanel />
        </div>
      )}

      {activeTab === 'noten' && canConfigureNotenschemata && (
        <div className="bg-white shadow rounded-lg p-6">
          <GradeSchemasPanel isAdmin={isAdmin} />
        </div>
      )}

      {/* Users Management Tab */}
      {activeTab === 'users' && canManageUsers && (
        <div className="space-y-6">
          {/* User Statistics */}
          {userStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Gesamt</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.total}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Shield className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">LDAP</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.ldap}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Activity className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Aktiv (7T)</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.recentLogins}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserX className="h-6 w-6 text-red-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Deaktiviert</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.inactive}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Award className="h-6 w-6 text-purple-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Multi-Rollen</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.multiRole}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-6 w-6 text-indigo-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Rollen-Zuw.</dt>
                        <dd className="text-lg font-medium text-gray-900">{userStats.totalRoleAssignments}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filter */}
          <div className="card p-6">
            <ListToolbar
              title="Benutzerverwaltung"
              description="Suche, Filter und Rollenverwaltung für Nutzerkonten."
              trailing={
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="inline-flex shrink-0 items-center gap-2"
                    disabled={ldapTeachersProvisioning}
                    onClick={handleLdapTeachersProvision}
                  >
                    <RefreshCw className={`h-4 w-4 ${ldapTeachersProvisioning ? 'animate-spin' : ''}`} aria-hidden />
                    {ldapTeachersProvisioning ? 'LDAP wird gelesen…' : 'LDAP Lehrer anlegen'}
                  </Button>
                  <label htmlFor="settings-user-search" className="sr-only">
                    Benutzer suchen
                  </label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      id="settings-user-search"
                      type="search"
                      placeholder="Benutzer suchen..."
                      className="input-field min-h-[2.25rem] w-full min-w-[14rem] py-2 pl-10 text-sm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    className="input-field min-h-[2.25rem] py-2 text-sm"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="">Alle Rollen</option>
                    <option value="admin">Administrator</option>
                    <option value="schulleitung">Schulleitung</option>
                    <option value="abteilungsleiter">Abteilungsleiter</option>
                    <option value="klassenlehrer">Klassenlehrer</option>
                    <option value="teacher">Lehrer</option>
                    <option value="student">Schüler</option>
                    <option value="guest">Gast</option>
                    <option value="pending_approval">Ausstehende Freischaltung</option>
                  </select>
                </>
              }
            />

            {/* Error Display */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Users Table */}
            <TableScrollArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Benutzer</TableHeaderCell>
                    <TableHeaderCell>Rolle</TableHeaderCell>
                    <TableHeaderCell>Typ</TableHeaderCell>
                    <TableHeaderCell>Letzter Login</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Rollen</TableHeaderCell>
                    <TableHeaderCell>Aktionen</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <EmptyTableRow colSpan={7} title="Lädt..." description="Benutzerdaten werden geladen." />
                  ) : users.length === 0 ? (
                    <EmptyTableRow colSpan={7} title="Keine Benutzer gefunden" description="Suche oder Filter anpassen." />
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id} className={user.is_active ? '' : 'bg-gray-50 opacity-60 dark:bg-gray-800/40'}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.username}
                              </div>
                              {user.email && (
                                <div className="text-sm text-gray-500">{user.email}</div>
                              )}
                              {(user.first_name || user.last_name) && (
                                <div className="text-sm text-gray-500">
                                  {user.first_name} {user.last_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.primary_role)}`}>
                            {getRoleDisplayName(user.primary_role)}
                          </span>
                          {user.additional_roles_count > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              +{user.additional_roles_count}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-gray-500">
                          <div className="flex flex-col space-y-1">
                            <div>
                              {user.auth_method === 'ldap' ? (
                                <Badge variant="info" className="inline-flex items-center gap-1">
                                  <Shield className="w-3 h-3" />
                                  LDAP
                                </Badge>
                              ) : (
                                <Badge variant="neutral">Lokal</Badge>
                              )}
                            </div>
                            {user.last_auth_method && user.last_auth_method !== user.auth_method && (
                              <div className="text-xs text-gray-400">
                                Letzter Login: {user.last_auth_method === 'ldap' ? 'LDAP' : 'Lokal'}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-gray-500">
                          {user.last_login ? formatDate(user.last_login) : 'Nie'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {user.registration_status === 'pending' ? (
                            <Badge variant="warning" className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Wartet auf Freischaltung
                            </Badge>
                          ) : user.registration_status === 'rejected' ? (
                            <Badge variant="danger" className="inline-flex items-center gap-1">
                              <UserX className="w-3 h-3" />
                              Abgelehnt
                            </Badge>
                          ) : user.is_active ? (
                            <Badge variant="success" className="inline-flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Aktiv
                            </Badge>
                          ) : (
                            <Badge variant="danger" className="inline-flex items-center gap-1">
                              <UserX className="w-3 h-3" />
                              Deaktiviert
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => openRoleModal(user)}
                              size="xs"
                              variant="outline"
                              className="inline-flex items-center"
                            >
                              <Award className="w-3 h-3 mr-1" />
                              {user.additional_roles_count > 0 ? `${user.additional_roles_count + 1} Rollen` : 'Rollen verwalten'}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => openRoleModal(user)}
                              size="xs"
                              variant="outline"
                              className="inline-flex items-center"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Rollen
                            </Button>
                            {user.registration_status === 'pending' ? (
                              <>
                                <Button
                                  onClick={() => approveUser(user.id)}
                                  size="xs"
                                  variant="secondary"
                                  className="inline-flex items-center"
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Freischalten
                                </Button>
                                <Button
                                  onClick={() => rejectUser(user.id)}
                                  size="xs"
                                  variant="danger"
                                  className="inline-flex items-center"
                                >
                                  <UserX className="w-4 h-4 mr-1" />
                                  Ablehnen
                                </Button>
                              </>
                            ) : (
                              <Button
                                onClick={() => toggleUserStatus(user.id, user.is_active)}
                                size="xs"
                                variant={user.is_active ? 'danger' : 'secondary'}
                                className="inline-flex items-center"
                              >
                                {user.is_active ? (
                                  <>
                                    <UserX className="w-4 h-4 mr-1" />
                                    Deaktivieren
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-4 h-4 mr-1" />
                                    Aktivieren
                                  </>
                                )}
                              </Button>
                            )}
                            <Button
                              onClick={() => resetUserMfa(user.username)}
                              size="xs"
                              variant="secondary"
                              className="inline-flex items-center"
                            >
                              <Key className="w-4 h-4 mr-1" />
                              OTP zurücksetzen
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableScrollArea>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Rollen verwalten: {selectedUser.username}
                </h3>
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Current Roles */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Aktuelle Rollen</h4>
                  
                  {/* Primary Role */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getRoleIcon(selectedUser.primary_role)}</span>
                        <div>
                          <span className="text-sm font-medium text-blue-900">
                            {getRoleDisplayName(selectedUser.primary_role)} (Primär)
                          </span>
                          <p className="text-xs text-blue-700 mt-1">
                            Diese Rolle kann nicht entfernt werden
                          </p>
                        </div>
                      </div>
                      <Shield className="h-5 w-5 text-blue-500" />
                    </div>
                  </div>

                  {/* Additional Roles */}
                  <div className="space-y-2">
                    {userRoles.filter(role => role.name !== 'klassenlehrer').length === 0 ? (
                      <p className="text-sm text-gray-500">Keine zusätzlichen Rollen zugewiesen</p>
                    ) : (
                      userRoles
                        .filter(role => role.name !== 'klassenlehrer') // Hide klassenlehrer here, show in specific assignments
                        .map((role) => {
                        const roleId = getUserRoleId(role);
                        return (
                        <div key={`${role.name}-${roleId ?? 'no-id'}-${role.granted_at}`} className={`p-3 rounded-md border ${
                          role.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 flex-1">
                              <span className="text-lg">{getRoleIcon(role.name)}</span>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-medium ${
                                    role.is_active ? 'text-green-900' : 'text-gray-600'
                                  }`}>
                                    {role.display_name}
                                  </span>
                                  {!role.is_active && (
                                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                      Deaktiviert
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  Zugewiesen: {formatDate(role.granted_at)}
                                  {role.expires_at && (
                                    <span> • Läuft ab: {formatDate(role.expires_at)}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {/* Show delete button for all active roles (except primary role) */}
                            {role.is_active && roleId && role.name !== selectedUser.primary_role && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Möchten Sie die Rolle "${role.display_name}" wirklich entziehen?`)) {
                                    revokeRole(selectedUser.id, roleId);
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="Rolle entziehen"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )})
                    )}
                    
                    {/* Show note for klassenlehrer role */}
                    {userRoles.some(role => role.name === 'klassenlehrer') && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">📚</span>
                          <div>
                            <p className="text-sm text-blue-900 font-medium">Klassenlehrer-Zuweisungen</p>
                            <p className="text-xs text-blue-700">
                              Verwalten Sie Klassenzuweisungen unter "Spezifische Zuweisungen" →
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Roles */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Verfügbare Rollen</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {availableRoles.map((role) => {
                      const isAssigned = userRoles.some(ur => ur.name === role.name && ur.is_active) || 
                                       selectedUser.primary_role === role.name;
                      
                      return (
                        <div key={role.id} className={`p-3 rounded-md border ${
                          isAssigned ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:border-purple-300'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 flex-1">
                              <span className="text-lg">{getRoleIcon(role.name)}</span>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-medium ${
                                    isAssigned ? 'text-gray-500' : 'text-gray-900'
                                  }`}>
                                    {role.display_name}
                                  </span>
                                  {isAssigned && (
                                    <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded">
                                      Zugewiesen
                                    </span>
                                  )}
                                </div>
                                {role.description && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    {role.description}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {role.permissions.slice(0, 3).map((permission, index) => (
                                    <span key={index} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                      {permission === '*' ? 'Alle Berechtigungen' : permission}
                                    </span>
                                  ))}
                                  {role.permissions.length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{role.permissions.length - 3} weitere
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {!isAssigned && (
                              <button
                                onClick={() => assignRole(selectedUser.id, role.id)}
                                className="text-purple-600 hover:text-purple-800 p-1"
                                title="Rolle zuweisen"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Specific Assignments */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Spezifische Zuweisungen</h4>
                  
                  {/* Debug Info */}
                  <div className="mb-4 p-2 bg-gray-50 rounded text-xs text-gray-600">
                    Debug: {availableClasses.length} Klassen, {availableSubjects.length} Fächer, {availableDepartments.length} Abteilungen geladen
                  </div>
                  
                  <div className="space-y-4">
                    {/* Class Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        📚 Klassenlehrer
                      </h5>
                      <div className="space-y-2">
                        {specificAssignments
                          .filter(a => a.assignment_type === 'class' && a.is_active)
                          .map((assignment, index) => (
                            <div key={index} className="p-2 bg-cyan-50 border border-cyan-200 rounded text-sm">
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-cyan-900">
                                  Klasse {assignment.assignment_value}
                                </span>
                                <button
                                  onClick={() => revokeSpecificRole(selectedUser.id, 'klassenlehrer', 'class', assignment.assignment_value)}
                                  className="text-red-600 hover:text-red-800"
                                  title={`Klassenlehrer-Rolle für ${assignment.assignment_value} entziehen`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        
                        {/* Add Class Assignment */}
                        <div className="p-2 border-2 border-dashed border-gray-300 rounded">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                assignSpecificRole(selectedUser.id, 'klassenlehrer', 'class', e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="w-full text-sm border-none bg-transparent"
                            disabled={availableClasses.length === 0}
                          >
                            <option value="">
                              {availableClasses.length === 0 ? 'Lädt Klassen...' : '+ Klasse zuweisen'}
                            </option>
                            {availableClasses
                              .filter(cls => !specificAssignments.some(a => 
                                a.assignment_type === 'class' && 
                                a.assignment_value === cls.class_name && 
                                a.is_active
                              ))
                              .map(cls => (
                                <option key={cls.id} value={cls.class_name}>
                                  {cls.class_name} - {cls.student_count || 0} Schüler
                                </option>
                              ))}
                          </select>
                          {availableClasses.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Klassen werden geladen...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Department Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        🏢 Abteilungsleiter
                      </h5>
                      <div className="space-y-2">
                        {specificAssignments
                          .filter(a => a.assignment_type === 'department' && a.is_active)
                          .map((assignment, index) => (
                            <div key={index} className="p-2 bg-indigo-50 border border-indigo-200 rounded text-sm">
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-indigo-900">
                                  {assignment.assignment_value}
                                </span>
                                <button
                                  onClick={() => revokeSpecificRole(selectedUser.id, 'abteilungsleiter', 'department', assignment.assignment_value)}
                                  className="text-red-600 hover:text-red-800"
                                  title={`Abteilungsleiter-Rolle für ${assignment.assignment_value} entziehen`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        
                        <div className="p-2 border-2 border-dashed border-gray-300 rounded">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                assignSpecificRole(selectedUser.id, 'abteilungsleiter', 'department', e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="w-full text-sm border-none bg-transparent"
                            disabled={availableDepartments.length === 0}
                          >
                            <option value="">
                              {availableDepartments.length === 0 ? 'Lädt Abteilungen...' : '+ Abteilung zuweisen'}
                            </option>
                            {availableDepartments
                              .filter(dept => !specificAssignments.some(a => 
                                a.assignment_type === 'department' && 
                                a.assignment_value === dept && 
                                a.is_active
                              ))
                              .map(dept => (
                                <option key={dept} value={dept}>
                                  {dept}
                                </option>
                              ))}
                          </select>
                          {availableDepartments.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Abteilungen werden geladen...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Subject Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        📖 Fachlehrer
                      </h5>
                      <div className="space-y-2">
                        {specificAssignments
                          .filter(a => a.assignment_type === 'subject' && a.is_active)
                          .map((assignment, index) => (
                            <div key={index} className="p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-blue-900">
                                  {assignment.assignment_value}
                                </span>
                                <button
                                  onClick={() => revokeSpecificRole(selectedUser.id, 'teacher', 'subject', assignment.assignment_value)}
                                  className="text-red-600 hover:text-red-800"
                                  title={`Fachlehrer-Rolle für ${assignment.assignment_value} entziehen`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        
                        <div className="p-2 border-2 border-dashed border-gray-300 rounded">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                assignSpecificRole(selectedUser.id, 'teacher', 'subject', e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="w-full text-sm border-none bg-transparent"
                            disabled={availableSubjects.length === 0}
                          >
                            <option value="">
                              {availableSubjects.length === 0 ? 'Lädt Fächer...' : '+ Fach zuweisen'}
                            </option>
                            {availableSubjects
                              .filter(subj => !specificAssignments.some(a => 
                                a.assignment_type === 'subject' && 
                                a.assignment_value === subj.subject_name && 
                                a.is_active
                              ))
                              .map(subj => (
                                <option key={subj.id} value={subj.subject_name}>
                                  {subj.subject_name} ({subj.department})
                                </option>
                              ))}
                          </select>
                          {availableSubjects.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Fächer werden geladen...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Geldkonten Settings Tab */}
      {activeTab === 'geldkonten' && isAdmin && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <CreditCard className="h-6 w-6 mr-2 text-blue-600" />
                  Geldkonten-Konfiguration
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Konfigurieren Sie PayPal, SEPA und FinTS für die Institution
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={testPaypalConnection}
                  disabled={testingConnection || testingFintsConnection || paypalLoading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  {testingConnection ? 'Teste...' : 'PayPal testen'}
                </button>
                <button
                  type="button"
                  onClick={testFintsConnection}
                  disabled={
                    testingConnection ||
                    testingFintsConnection ||
                    paypalLoading ||
                    !paypalSettings ||
                    !!fintsTestTanToken
                  }
                  className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  {testingFintsConnection ? 'Kontoauszug...' : 'FinTS Kontoauszug testen'}
                </button>
              </div>
            </div>

            {paypalSettings === null ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Lädt Geldkonten-Einstellungen...</p>
              </div>
            ) : (
              <>
                {geldkontenFetchError && (
                  <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-medium">Serverabruf der gespeicherten Geldkonten-Daten fehlgeschlagen</p>
                    <p className="mt-1">{geldkontenFetchError}</p>
                    <p className="mt-2 text-amber-800">
                      Sie können PayPal-, SEPA- und FinTS-Einstellungen trotzdem bearbeiten und speichern (gespeicherte Werte werden dann wieder vom Server geladen).
                    </p>
                  </div>
                )}
              <form
                id="geldkonten-settings-form"
                onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const settings = {
                  paypal_api_url: formData.get('paypal_api_url'),
                  paypal_client_id: formData.get('paypal_client_id'),
                  paypal_business_name: formData.get('paypal_business_name'),
                  paypal_currency: formData.get('paypal_currency'),
                  paypal_return_url: formData.get('paypal_return_url'),
                  paypal_cancel_url: formData.get('paypal_cancel_url'),
                  paypal_webhook_url: formData.get('paypal_webhook_url'),
                  paypal_me_username: formData.get('paypal_me_username'),
                  sepa_creditor_name: formData.get('sepa_creditor_name'),
                  sepa_iban: formData.get('sepa_iban'),
                  sepa_bic: formData.get('sepa_bic'),
                  fints_url: formData.get('fints_url'),
                  fints_blz: formData.get('fints_blz'),
                  fints_user_id: formData.get('fints_user_id'),
                  fints_account_iban: formData.get('fints_account_iban'),
                  fints_product_id: formData.get('fints_product_id'),
                  fints_security_function: formData.get('fints_security_function'),
                  fints_tan_medium_name: formData.get('fints_tan_medium_name'),
                  fints_customer_id: formData.get('fints_customer_id'),
                };
                updatePaypalSettings(settings);
              }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* PayPal API URL */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal API URL
                    </label>
                    <input
                      type="url"
                      name="paypal_api_url"
                      defaultValue={paypalSettings.paypal_api_url?.value || 'https://api.sandbox.paypal.com'}
                      list="paypal_api_urls"
                      placeholder="https://api.sandbox.paypal.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <datalist id="paypal_api_urls">
                      <option value="https://api.sandbox.paypal.com" />
                      <option value="https://api.paypal.com" />
                    </datalist>
                    <p className="text-xs text-gray-500 mt-1">
                      Eigene URL möglich (z. B. Sandbox/Live). Voreinstellungen per Dropdown nutzbar.
                    </p>
                  </div>

                  {/* Business Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Geschäftsname
                    </label>
                    <input
                      type="text"
                      name="paypal_business_name"
                      defaultValue={paypalSettings.paypal_business_name?.value || ''}
                      placeholder="Lernrakete"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Name der Institution für Zahlungsaufforderungen
                    </p>
                  </div>

                  {/* Client ID */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal Client ID
                    </label>
                    <input
                      type="text"
                      name="paypal_client_id"
                      defaultValue={paypalSettings.paypal_client_id?.value || ''}
                      placeholder="AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQq..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Client ID aus der PayPal Developer Console
                    </p>
                  </div>

                  {/* Client Secret */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal Client Secret
                    </label>
                    <input
                      type="password"
                      value={paypalClientSecret}
                      onChange={(e) => setPaypalClientSecret(e.target.value)}
                      placeholder="Leer lassen, um unverändert zu lassen"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Client Secret aus der PayPal Developer Console (wird verschlüsselt gespeichert)
                    </p>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Standard-Währung
                    </label>
                    <select
                      name="paypal_currency"
                      defaultValue={paypalSettings.paypal_currency?.value || 'EUR'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="EUR">EUR (Euro)</option>
                      <option value="USD">USD (US-Dollar)</option>
                      <option value="GBP">GBP (Britisches Pfund)</option>
                      <option value="CHF">CHF (Schweizer Franken)</option>
                    </select>
                  </div>

                  {/* Return URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Erfolgs-URL
                    </label>
                    <input
                      type="url"
                      name="paypal_return_url"
                      defaultValue={paypalSettings.paypal_return_url?.value || ''}
                      placeholder="https://ihr-domain.de/payments/success"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Cancel URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Abbruch-URL
                    </label>
                    <input
                      type="url"
                      name="paypal_cancel_url"
                      defaultValue={paypalSettings.paypal_cancel_url?.value || ''}
                      placeholder="https://ihr-domain.de/payments/cancelled"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Webhook URL */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook-URL
                    </label>
                    <input
                      type="url"
                      name="paypal_webhook_url"
                      defaultValue={paypalSettings.paypal_webhook_url?.value || ''}
                      placeholder="https://ihr-domain.de/api/payments/webhook"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      URL für PayPal-Webhooks (Echtzeit-Benachrichtigungen)
                    </p>
                  </div>

                  {/* PayPal.me Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal.me Benutzername
                    </label>
                    <input
                      type="text"
                      name="paypal_me_username"
                      defaultValue={paypalSettings.paypal_me_username?.value || ''}
                      placeholder="IhrPayPalUsername"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Wird für PayPal.me Links genutzt (https://paypal.me/Benutzername)
                    </p>
                  </div>

                  <div className="md:col-span-2 border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">SEPA / EPC-QR</h4>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zahlungsempfänger (Name)
                    </label>
                    <input
                      type="text"
                      name="sepa_creditor_name"
                      defaultValue={paypalSettings.sepa_creditor_name?.value || ''}
                      placeholder="Lernrakete"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEPA-IBAN
                    </label>
                    <input
                      type="text"
                      name="sepa_iban"
                      defaultValue={paypalSettings.sepa_iban?.value || ''}
                      placeholder="DE89..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEPA-BIC (optional)
                    </label>
                    <input
                      type="text"
                      name="sepa_bic"
                      defaultValue={paypalSettings.sepa_bic?.value || ''}
                      placeholder="GENODE..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div className="md:col-span-2 border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">FinTS (Girokonto)</h4>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank suchen (wie bei Hibiscus / HBCI4Java)
                    </label>
                    <div className="relative">
                      <input
                        type="search"
                        value={fintsBankSearchQuery}
                        onChange={(e) => setFintsBankSearchQuery(e.target.value)}
                        placeholder="Name oder Ort (z. B. Sparkasse Aachen) oder 8-stellige BLZ"
                        autoComplete="off"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      {fintsBankSearchLoading && (
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400">Suche…</span>
                      )}
                    </div>
                    {fintsBankHits.length > 0 && (
                      <ul className="mt-2 max-h-52 overflow-auto rounded-md border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
                        {fintsBankHits.map((b) => (
                          <li key={`${b.blz}-${b.url}`}>
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                              onClick={() => applyFintsBankPick(b)}
                            >
                              <span className="font-medium text-gray-900">{b.name}</span>
                              <span className="text-gray-600"> · {b.ort}</span>
                              <span className="block font-mono text-xs text-gray-500 mt-0.5">
                                BLZ {b.blz} · {b.bic}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Datenbasis: HBCI4Java <span className="font-mono">blz.properties</span> — dieselbe Bankliste wie bei Hibiscus.
                      Es werden nur Institute mit bekannter HTTPS-FinTS-Adresse angezeigt.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      FinTS-Server URL
                    </label>
                    <input
                      ref={fintsUrlInputRef}
                      type="url"
                      name="fints_url"
                      defaultValue={paypalSettings.fints_url?.value || ''}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      BLZ
                    </label>
                    <input
                      ref={fintsBlzInputRef}
                      type="text"
                      name="fints_blz"
                      defaultValue={paypalSettings.fints_blz?.value || ''}
                      placeholder="12345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      FinTS Benutzerkennung
                    </label>
                    <input
                      type="text"
                      name="fints_user_id"
                      defaultValue={paypalSettings.fints_user_id?.value || ''}
                      placeholder="Benutzerkennung"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      FinTS Kunden-ID (optional)
                    </label>
                    <input
                      type="text"
                      name="fints_customer_id"
                      defaultValue={paypalSettings.fints_customer_id?.value || ''}
                      placeholder="Nur falls die Bank eine Kunden-ID verlangt (HKIDN)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Wird in den Geldkonten-Einstellungen gespeichert und bei jedem FinTS-Abruf an den Python-Dienst
                      mitgeschickt. Ohne Bankvorgabe leer lassen.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Konto-IBAN für Abruf (optional)
                    </label>
                    <input
                      type="text"
                      name="fints_account_iban"
                      defaultValue={paypalSettings.fints_account_iban?.value || ''}
                      placeholder="DE89..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      FinTS Produktkennung
                    </label>
                    <input
                      type="text"
                      name="fints_product_id"
                      defaultValue={paypalSettings.fints_product_id?.value || 'A44C2953982351617D475443E'}
                      placeholder="A44C2953982351617D475443E"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      TAN-Verfahren – Voreinstellung (optional)
                    </label>
                    <input
                      type="text"
                      name="fints_security_function"
                      defaultValue={paypalSettings.fints_security_function?.value || ''}
                      placeholder="z. B. 910/911 (chipTAN optisch), 913 (QR), SMS-Verfahren — leer = beim Abruf wählen"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      QR-/photoTAN (Sparkasse): oft <span className="font-mono">913</span> — wie in Hibiscus bei
                      „selected pintan method“. chipTAN optisch manuell: typ. <span className="font-mono">910</span>.
                      Mehrere TAN-Aufforderungen hintereinander sind normal (z. B. zwei DialogInitSCA wie bei Hibiscus);
                      jeden Schritt in Lernrakete einzeln mit TAN bestätigen. Leer lassen = Verfahren
                      erst beim Abruf wählen.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      TAN-Medium (optional)
                    </label>
                    <input
                      type="text"
                      name="fints_tan_medium_name"
                      defaultValue={paypalSettings.fints_tan_medium_name?.value || ''}
                      placeholder="Exakt wie in HBCI/Hibiscus, z. B. Sparkassen-Card (Debitkarte)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Oft auch bei QR-TAN (<span className="font-mono">913</span>) nötig; exakt wie im Hibiscus-Log unter
                      „TAN-Medienbezeichnungen empfangen“ (z. B. Sparkassen-Card).
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      FinTS PIN
                    </label>
                    <input
                      type="password"
                      value={fintsPin}
                      onChange={(e) => setFintsPin(e.target.value)}
                      placeholder="Leer lassen, um unverändert zu lassen"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-between">
                  <div className="text-sm text-gray-600">
                    <p><strong>Hinweis:</strong> Alle Änderungen werden sofort gespeichert.</p>
                    <p>Stellen Sie sicher, dass die URLs über HTTPS erreichbar sind.</p>
                  </div>
                  <button
                    type="submit"
                    disabled={paypalLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {paypalLoading ? 'Speichere...' : 'Einstellungen speichern'}
                  </button>
                </div>
              </form>
              </>
            )}

            <FintsTanChallengeModal
              open={Boolean(fintsTestTanToken)}
              contextTitle="FinTS Kontoauszug-Test"
              decoupled={fintsTestTanDecoupled}
              challengeText={fintsTestTanChallengeText || 'Die Bank fordert eine TAN oder Freigabe an.'}
              challengeMediaB64={fintsTestTanChallengeMediaB64}
              challengeMediaMime={fintsTestTanChallengeMediaMime}
              challengeHhduc={fintsTestTanChallengeHhduc}
              bankMediumName={fintsTestTanBankMediumName}
              procedureHint={fintsTestTanProcedureHint}
              methodOptions={fintsTestTanMethodOptions}
              selectedMethod={fintsTestSelectedTanMethod}
              onSelectedMethodChange={setFintsTestSelectedTanMethod}
              tanValue={fintsTestTanValue}
              onTanValueChange={setFintsTestTanValue}
              onSubmit={() => void submitFintsTestTan()}
              onAbort={() => {
                clearFintsTestTanUi();
                toast('FinTS-TAN-Test abgebrochen.');
              }}
              submitting={testingFintsConnection}
            />

            {/* FinTS Kontoauszug-Test Ergebnis */}
            {fintsTestResult && (
              <div
                className={`mt-6 rounded-md p-4 ${
                  fintsTestResult.success
                    ? 'border border-green-200 bg-green-50'
                    : 'border border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start">
                  <div className={`flex-shrink-0 ${fintsTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {fintsTestResult.success ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <XCircle className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3
                      className={`text-sm font-medium ${
                        fintsTestResult.success ? 'text-green-800' : 'text-red-800'
                      }`}
                    >
                      {fintsTestResult.success ? 'FinTS: Kontoauszug OK' : 'FinTS: Test fehlgeschlagen'}
                    </h3>
                    <div
                      className={`mt-1 text-sm ${fintsTestResult.success ? 'text-green-700' : 'text-red-700'}`}
                    >
                      <p>{fintsTestResult.message}</p>
                      {fintsTestResult.hint && <p className="mt-2">{fintsTestResult.hint}</p>}
                      {fintsTestResult.success &&
                        Array.isArray(fintsTestResult.transactions_preview) &&
                        fintsTestResult.transactions_preview.length > 0 && (
                          <ul className="mt-2 max-h-48 list-inside list-disc space-y-0.5 overflow-y-auto text-xs">
                            {fintsTestResult.transactions_preview.map((row: any, idx: number) => (
                              <li key={idx}>
                                {row.booking_date || '—'} · {row.amount ?? '—'} {row.currency || 'EUR'}
                                {row.counterpart_name ? ` · ${row.counterpart_name}` : ''}
                                {row.purpose ? ` — ${String(row.purpose).slice(0, 80)}` : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Connection Test Result */}
            {connectionTestResult && (
              <div className={`mt-6 p-4 rounded-md ${
                connectionTestResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${
                    connectionTestResult.success ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {connectionTestResult.success ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <XCircle className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${
                      connectionTestResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {connectionTestResult.success ? 'PayPal: Verbindung erfolgreich' : 'PayPal: Verbindung fehlgeschlagen'}
                    </h3>
                    <div className={`mt-1 text-sm ${
                      connectionTestResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <p>{connectionTestResult.message}</p>
                      {connectionTestResult.success && connectionTestResult.data && (
                        <div className="mt-2 text-xs">
                          <p>API URL: {connectionTestResult.data.api_url}</p>
                          <p>Client ID: {connectionTestResult.data.client_id}</p>
                          <p>Zeitstempel: {new Date(connectionTestResult.data.timestamp).toLocaleString('de-DE')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="mt-8 p-4 bg-blue-50 rounded-md">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                💡 Geldkonten einrichten
              </h3>
              <div className="text-sm text-blue-800 space-y-1">
                <p>1. <strong>PayPal Developer Account</strong> erstellen: <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="underline">developer.paypal.com</a></p>
                <p>2. <strong>App erstellen</strong> und Client ID + Secret kopieren</p>
                <p>3. <strong>Webhook-Events</strong> konfigurieren: PAYMENT.CAPTURE.COMPLETED, PAYMENT.CAPTURE.DENIED</p>
                <p>4. <strong>SEPA- und FinTS-Daten</strong> (IBAN/BIC, FinTS URL, BLZ, Benutzerkennung) ergänzen</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Signature Tab */}
      {activeTab === 'email-signature' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Mail className="h-6 w-6 mr-2 text-blue-600" />
                  E-Mail-Signatur
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Konfigurieren Sie Ihre persönliche E-Mail-Signatur
                </p>
              </div>
            </div>

            {emailSignatureLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Lädt Signatur-Einstellungen...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* E-Mail-Inhaltsprüfung (Hinweis vor dem Versand) */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="email-content-check"
                      checked={emailContentCheckEnabled}
                      disabled={emailContentCheckSaving}
                      onChange={async (e) => {
                        const next = e.target.checked;
                        setEmailContentCheckEnabled(next);
                        setEmailContentCheckSaving(true);
                        try {
                          await api.put('/settings/email-content-check', { enabled: next });
                          toast.success(next ? 'Inhaltsprüfung aktiviert' : 'Inhaltsprüfung deaktiviert');
                        } catch (err: any) {
                          setEmailContentCheckEnabled(!next);
                          toast.error(err?.response?.data?.message || 'Speichern fehlgeschlagen');
                        } finally {
                          setEmailContentCheckSaving(false);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="email-content-check" className="ml-2 text-sm font-medium text-gray-700">
                      E-Mail-Inhaltsprüfung vor dem Versand
                    </label>
                    {emailContentCheckSaving && <span className="ml-2 text-xs text-gray-500">Speichern…</span>}
                  </div>
                  <p className="mt-1 ml-6 text-xs text-gray-500">
                    Hinweis anzeigen, wenn der Inhalt eskalierend oder unprofessionell wirkt (mit Optionen: Trotzdem senden, Verbessern, Abbrechen). Beim Hinweis können Sie einzelne Empfänger von der Prüfung ausnehmen; Ausnahmen können Sie hier wieder aufheben.
                  </p>
                  {emailContentCheckEnabled && contentCheckExcludedRecipients.length > 0 && (
                    <div className="mt-4 ml-6">
                      <p className="text-xs font-medium text-gray-700 mb-2">Von der Prüfung ausgenommene Empfänger (Ausnahmen rückgängig machen):</p>
                      <ul className="space-y-1.5">
                        {contentCheckExcludedRecipients.map((email) => (
                          <li key={email} className="flex items-center justify-between gap-2 text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">
                            <span className="truncate">{email}</span>
                            <button
                              type="button"
                              disabled={contentCheckExclusionsSaving}
                              onClick={async () => {
                                setContentCheckExclusionsSaving(true);
                                try {
                                  const newList = contentCheckExcludedRecipients.filter((e) => e !== email);
                                  await api.put('/settings/email-content-check-exclusions', { excludedRecipients: newList });
                                  setContentCheckExcludedRecipients(newList);
                                  toast.success('Ausnahme entfernt');
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.message || 'Entfernen fehlgeschlagen');
                                } finally {
                                  setContentCheckExclusionsSaving(false);
                                }
                              }}
                              className="shrink-0 text-red-600 hover:text-red-800 font-medium"
                              title="Ausnahme entfernen"
                            >
                              Entfernen
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Enable/Disable Signature */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="signature-enabled"
                    checked={emailSignature.enabled}
                    onChange={(e) => setEmailSignature({ ...emailSignature, enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="signature-enabled" className="ml-2 text-sm font-medium text-gray-700">
                    Signatur automatisch zu E-Mails hinzufügen
                  </label>
                </div>

                {/* Text Signature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text-Signatur
                  </label>
                  <textarea
                    value={emailSignature.text}
                    onChange={(e) => setEmailSignature({ ...emailSignature, text: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ihre Signatur im Text-Format (wird für Text-E-Mails verwendet)"
                  />
                </div>

                {/* HTML Signature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTML-Signatur
                  </label>
                  <textarea
                    value={emailSignature.html}
                    onChange={(e) => setEmailSignature({ ...emailSignature, html: e.target.value })}
                    className="w-full min-h-[150px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Ihre Signatur im HTML-Format (wird für HTML-E-Mails verwendet)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    HTML-Tags sind erlaubt (z.B. &lt;strong&gt;, &lt;em&gt;, &lt;br&gt;)
                  </p>
                </div>

                {/* Signature Image URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signatur-Bild (URL)
                  </label>
                  <input
                    type="url"
                    value={emailSignature.imageUrl}
                    onChange={(e) => setEmailSignature({ ...emailSignature, imageUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/signature.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: URL zu einem Bild für Ihre Signatur (z.B. Logo oder Unterschrift)
                  </p>
                  {emailSignature.imageUrl && (
                    <div className="mt-2">
                      <img
                        src={emailSignature.imageUrl}
                        alt="Signatur-Vorschau"
                        className="max-w-xs max-h-32 border border-gray-300 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={saveEmailSignature}
                    disabled={emailSignatureSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {emailSignatureSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Signatur speichern
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Credential Modal */}
      <EmailCredentialModal
        isOpen={showEmailCredentialModal}
        onClose={() => {
          setShowEmailCredentialModal(false);
          setEditingEmailCredential(null);
        }}
        onSave={saveUserEmailCredentials}
        credential={editingEmailCredential}
        requiredForSending={false}
      />
    </div>
  );
}
